/**
 * Chatvenster (`/conversations/show/:id`) — het scherm waar chatters in werken.
 *
 * Gemeten aan het oude scherm (scratchpad/chatwin.py, conversatie 17364):
 *
 *   kopblok        x=281 w=1598 h=224   model, tag, tellers, schakelaars
 *   berichten      x=281 w= 966         de bubbels + het invoerveld
 *   fan-paneel     x=1259 w= 620        Fan info, Model info, Spending behavior
 *
 * Eén endpoint voedt alles: GET /conversations/{id} geeft `conversation`,
 * `messages`, `total_message_count` en `has_more`. Het oude scherm pollt dat
 * elke ~1,2 s; dat ritme is hier overgenomen.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PPV_MIN_F2F_EUR, PPV_MIN_FC_CREDITS, kiezerPrijsFout } from '../lib/massPrijs'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Avatar, ErrorNote, Loading, Plat, Tag, Wacht } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { useAiStand } from '../lib/aiStatus'
import { Modal } from '../components/Modal'
import PersonaGids from '../components/PersonaGids'
import { ContentPicker } from '../components/ContentPicker'
import { FanHistorie, FanTransacties } from '../components/FanPaneel'
import { alleModellen, isSpender, perModel } from '../lib/spendAlleModellen'
import { FanInfoPaneel } from '../components/FanInfoPaneel'
import { ImportantNotes, ModelBovenblok, ModelInfoSectie, ModelKop, useModelInfoEngels } from '../components/ModelInfoPaneel'
import { EmojiKnop } from '../components/EmojiKnop'
import { api, chatMediaUrl } from '../lib/api'
import { isBeheer, isVolledigBeheer, magAiSchakelen, magBlokkeren, magPriveBeheer, magTimewaster, magVip, magZelfPrive, useMij } from '../lib/rechten'
import { useBijRand } from '../lib/scrollLaden'
import { useMobiel } from '../lib/mobiel'
import { MobielChatKop } from '../components/MobielChatKop'
import { NieuweAbonneeTag } from '../components/NieuweAbonneeTag'
import type { QueueRow } from '../lib/panels'
import { MobielBevestig } from '../components/MobielBevestig'
import type { MobielPaneel } from '../components/MobielChatKop'
import { credits, dtSec, dtShort, eur, num } from '../lib/format'
import { foutTekst, isVertaalFout, keuzeMenu, knopStand, MELD_VIA_TELEGRAM, terugval } from '../lib/vertaalKnop'
import type { Keuze, VertaalStandaard } from '../lib/vertaalKnop'
import { isAantoonbaarOnverzonden, verbindingBalkTekst, sindsTijd } from '../lib/verbinding'
import { massDmLabel } from '../lib/massVoortgang'
import { useInboxOpen } from '../lib/inboxOpen'
import { fooien as fooienVan } from '../lib/fanPaneel'
import { magStaartVragen, staartMagNu, staartSleutel } from '../lib/f2fStaart'
import type { Bedrag, Transactie, Verzending } from '../lib/fanPaneel'

/** Het maximum dat /conversations/{id} per keer teruggeeft (message_limit le=500). */
const MAX_BERICHTEN = 500

/** Een tijd uit de API zonder zone is UTC; zonder deze stap leest de browser
 *  hem als lokale tijd en valt de sprong vanaf Earnings twee uur mis. */
const tijdUtc = (v: string): string =>
  /[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v.replace(' ', 'T')}Z`

interface Message {
  id: number
  sender: string
  /** Alle bestanden van één verzending delen dit id; ze horen als ÉÉN bericht
   *  in beeld, net als op het platform zelf. 17-09-2026 (HERSTEL-007). */
  bundle_id?: string | null
  /** Alleen lokaal gevuld: de overige bijlagen van dezelfde bundel. */
  bundelMedia?: { url: string | null; type: string | null }[]
  content: string | null
  content_translation: string | null
  /** De Engelse lezing van dit bericht (ook bij uitgaand van het platform). */
  content_english?: string | null
  /** 'en' = de server bepaalde offline dat vertalen niet nodig is. */
  detected_language?: string | null
  created_at: string
  delivered: boolean | null
  delivery_error: string | null
  media_type: string | null
  media_url: string | null
  is_paid: boolean | null
  is_unlocked: boolean | null
  price: number | null
  /** 21-10-2026 (Jordy): bedrag als dit een tip-aanvraag is. */
  tip_request_price?: number | null
  sender_admin_name: string | null
  sender_chatter_name: string | null
  platform_message_id: string | null
  mass_message_log_id: number | null
  /** 06-10-2026: soort campagne, voor "Mass DM · Platform" / "· Dashboard". */
  mass_message_kind?: string | null
  /** 08-10-2026: "welcome" = automatisch welkomstbericht van het platform. */
  kind?: string | null
  /** 01-10-2026: wat de Mass DM-campagne werkelijk verstuurde, uit onze eigen
   *  opslag (bibliotheek of eigen miniatuur). Zie `_load_mass_campaigns`. */
  mass_media?: { url: string; type: string | null }[] | null
  was_translated: boolean | null
  auto_hidden: boolean | null
  event_type: string | null
  /** Het moment dat de fan betaalde (socket of aankoopscan). */
  unlocked_at?: string | null
  /** Op een unlockregel: het bericht dat is vrijgekocht. */
  unlocks_message_id?: number | null
}

interface Conversation {
  id: number
  /** ACTIVE / ARCHIVED — telt mee of de AI voor deze chat aanstaat. */
  status: string | null
  platform: string
  model_slug: string
  stage_name: string | null
  avatar_url: string | null
  model_persona_id: number
  model_info: Record<string, string> | null
  model_notes: string | null
  fancentro_model_id: string | null
  f2f_model_id: string | null
  fan_id: string
  fan_username: string | null
  fan_display_name: string | null
  fan_custom_notes: string | null
  /** Losse profielvelden over de fan; leeg zolang er niets is ingevuld. */
  fan_info?: Record<string, unknown> | null
  fan_engagement_level: string | null
  fan_first_seen_at: string | null
  fan_last_active_at: string | null
  fan_subscription_status: string | null
  fan_subscription_since: string | null
  /** 23-10-2026: betaalmoment van het nieuwste abonnement (revenue_events). */
  fan_subscription_last_paid_at?: string | null
  fan_subscription_renews: string | null
  fan_timezone: string | null
  /** Wanneer de fan voor het laatst iets stuurde; bepaalt de wachttijd. */
  last_fan_message_at: string | null
  fan_detected_language: string | null
  /** 06-10-2026: standaard van de uitgaande vertaalknop, met reden (server). */
  outgoing_translation_default?: VertaalStandaard | null
  fan_total_spend: number
  fan_total_spend_all_models: number
  fan_highest_purchase: number
  fan_lowest_purchase: number
  fan_last_payment_at: string | null
  new_paid_subscriber_at?: string | null
  new_paid_subscriber_amount?: number | null
  fan_online: boolean | null
  fan_spend_mix: Record<string, { amount: number; count: number }> | null
  fan_spend_by_model: { model_slug: string; stage_name: string; amount: number }[] | null
  /** 15-10-2026: tabblad Transactions zoals oud ("Spent on", fooien). */
  fan_spend_by_category?: Record<string, Bedrag> | null
  fan_spend_breakdown?: Record<string, Bedrag> | null
  fan_recent_transactions: Transactie[] | null
  /** 15-10-2026: tabblad History (Unlock history, Content history, Resend). */
  fan_content_sends?: Verzending[] | null
  fan_model_chats: { conversation_id: number; model_slug: string; model_name: string }[] | null
  current_tag: string | null
  total_messages: number
  fan_messages: number
  human_messages: number
  ai_messages: number
  ai_opted_in: boolean
  is_excluded_from_ai: boolean
  is_timewaster: boolean
  /** 21-10-2026 (Jordy): aparte vlag "Maybe timewaster". */
  is_maybe_timewaster?: boolean
  is_vip: boolean
  dashboard_blocked: boolean
  claimed_by_me: boolean
  claimed_by_name: string | null
  claimed_admin_ids: number[] | null
  created_at: string | null
  takeover_count: number
}

interface AssignableChatter {
  admin_user_id: number
  name: string
  username: string
  role: string
  is_me: boolean
}

interface Detail {
  conversation: Conversation
  messages: Message[]
  total_message_count: number
  has_more: boolean
  /** Heeft het platform nu een berichtenverbinding voor dit model? */
  messaging_connected?: boolean
  /** 04-10-2026 (Jordy): 'ok' | 'down' | 'unknown', uit data_freshness. Alleen
   *  'down' geeft de rode stip en de balk; 'unknown' zegt niets (geen alarm). */
  messaging_status?: string
  /** Laatste melding van de worker dat hij luisterde (alleen bij 'down'). */
  messaging_stale_since?: string | null
  /** Berichten waarvoor al een Resend-opdracht bestaat. */
  resent_message_ids?: number[]
  /** Wie de belangrijke notitie het laatst wijzigde, en wanneer. */
  notes_by?: string | null
  notes_at?: string | null
}

/** "FAN" / de naam van de chatter / "AI", zoals de kop boven elke bubbel. */
/**
 * Het afzenderlabel boven een bubbel, zoals oud het schrijft: de rol in
 * hoofdletters, en bij een chatter zijn naam erachter — "CHATTER · Max".
 * Nieuw toonde alleen de naam, waardoor niet te zien was of een bericht van
 * een mens of van de AI kwam.
 */
/** Een prijs zonder overbodige nullen: 800 blijft 800, 8.50 blijft 8.5. */
function prijs(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * Het label op een betaalde bijlage, precies zoals oud het rekent.
 *
 * Belangrijk: op FanCentro staat de prijs in CREDITS, niet in euro's — 800
 * credits is €8, geen €800. Nieuw zette er een euroteken voor en maakte van
 * een bijlage van acht euro er een van achthonderd. Alleen F2F rekent in
 * euro's.
 */
/** 12-10-2026 (Jordy): een afgeleverde PPV is niet te verwijderen (de server weigert het ook).
 *  Een PPV die nooit aankwam (fout, geen platform-id) mag nog weg om opnieuw te sturen. */
function afgeleverdePpv(m: Message): boolean {
  const betaald = !!m.is_paid || Number(m.price || 0) > 0
  const aangekomen = !!(m.platform_message_id || m.delivered) && !m.delivery_error
  return betaald && aangekomen
}

function ppvLabel(m: Message, platform: string): string | null {
  const p = Number(m.price || 0)
  const betaald = !!m.is_paid
  if (!betaald && !(p > 0)) return null
  const open = !!m.is_unlocked
  if (!(p > 0)) return open ? 'Unlocked' : 'Not paid'
  const f2f = String(platform || '').toLowerCase() === 'f2f'
  // 02-10-2026 (Jordy): credits met groepering ("4,000 credits"), ook op een Mass DM-bubbel.
  if (open) return f2f ? `Unlocked · €${prijs(p)}` : `Unlocked · ${credits(p)}`
  return f2f ? `Not paid · asking €${prijs(p)}` : `Not paid · asking ${credits(p)}`
}


/** De talen die het oude scherm bij naam noemt. */
const TALEN: Record<string, string> = {
  en: 'English', nl: 'Dutch', de: 'German', fr: 'French', es: 'Spanish',
  it: 'Italian', pt: 'Portuguese', tr: 'Turkish', pl: 'Polish', sv: 'Swedish',
  no: 'Norwegian', da: 'Danish', ar: 'Arabic', ru: 'Russian', ro: 'Romanian',
  cs: 'Czech', hu: 'Hungarian', el: 'Greek', fi: 'Finnish', id: 'Indonesian',
  ja: 'Japanese', ko: 'Korean', zh: 'Chinese', hi: 'Hindi', th: 'Thai',
}


function senderName(m: Message): string {
  const s = String(m.sender ?? '').toLowerCase()
  if (s === 'fan') return 'FAN'
  if (s === 'ai') return 'AI'
  // Zoals oud, en zo staat de ECHTE afzender op de bubbel: een chatter ->
  // "CHATTER · naam", een admin/eigenaar -> "ADMIN · naam" (niet als chatter
  // gelabeld), een Mass DM apart, en een bericht dat rechtstreeks op het
  // platform is getypt -> "SENT ON PLATFORM · CHATTER UNKNOWN".
  if (m.mass_message_log_id) {
    const naam = m.sender_chatter_name || m.sender_admin_name
    return naam ? `MASS DM · ${naam}` : 'DASHBOARD MASS DM'
  }
  if (m.sender_chatter_name) return `CHATTER · ${m.sender_chatter_name}`
  if (m.sender_admin_name) return `ADMIN · ${m.sender_admin_name}`
  if (m.kind === 'welcome') return 'PLATFORM · AUTOMATED'
  return 'SENT ON PLATFORM · CHATTER UNKNOWN'
}

/** Een bubbel die nog onderweg is: staat in beeld vóór de server hem kent. */
interface BezigBubbel {
  key: string
  tekst: string
  /** Lokale preview-URL van het gekozen bestand, als er media meegaat. */
  media?: string
  mediaSoort?: string
  /** De berichtrij die de server aanmaakte; zodra die in de lijst staat, weg. */
  serverId?: number
  fout?: string
  /** 06-10-2026: of deze poging vertaald werd verstuurd (voor opnieuw proberen). */
  vertaal?: boolean
  at: number
}

/**
 * Een bijlage in een bubbel. FanCentro- en F2F-links verlopen; als het plaatje
 * niet laadt toont het oude scherm "Photo (link expired on F2F)" in plaats van
 * een kapot icoon.
 */
function MessageMedia({ url, kind, platform }: {
  url: string | null
  kind: string | null
  platform: string
}) {
  const [broken, setBroken] = useState(!url)
  // 18-10-2026 (Jordy, F2F-fotofix A): een F2F-fanfoto komt pas ~2 s na het
  // openen binnen (refresh-media). De bubbel stond dan al op "kapot" en zette
  // dat nooit terug: "could not be played here" tot je herlaadde. Nu begint de
  // bubbel opnieuw zodra het adres verandert.
  useEffect(() => { setBroken(!url) }, [url])
  // Zolang het bestand nog onderweg kan zijn: "loading" in plaats van "no preview".
  const [geduld, setGeduld] = useState(!url)
  useEffect(() => {
    if (url) return
    setGeduld(true)
    const t = window.setTimeout(() => setGeduld(false), 15000)
    return () => window.clearTimeout(t)
  }, [url])
  const soort = String(kind || '').toLowerCase()
  const what = soort === 'video' ? 'Video' : soort === 'audio' ? 'Audio' : 'Photo'
  // 17-09-2026 (HERSTEL-007). "Link expired" was een aanname, en een onjuiste:
  // bij een bestand in ONZE eigen map (dat staat er gewoon, gecontroleerd op
  // schijf) betekende een laadfout alleen dat de browser het niet kon spelen —
  // bijvoorbeeld omdat hij die codec niet aankan of het token verlopen was.
  // Het scherm gooide dan de speler weg en beweerde dat de link dood was.
  // Nu: over een platformlink (http…) zeggen we nog steeds dat hij verlopen is,
  // want dat is daar de normale oorzaak. Over ons eigen bestand zeggen we wat
  // er aan de hand is en bieden we de bijlage aan om te openen.
  // 01-10-2026: ook de eigen kopie van een Mass DM (bibliotheek, eigen miniatuur).
  const eigenBestand = String(url || '').startsWith('/api/admin/')
  if (broken) {
    if (eigenBestand && url) {
      const src = chatMediaUrl(url) as string
      return (
        <div className="mut2" style={{ fontSize: 12, marginBottom: 4 }}>
          {what} could not be played here —{' '}
          <a href={src} target="_blank" rel="noopener noreferrer">open it</a>
        </div>
      )
    }
    // 01-10-2026: zonder link is "verlopen" niet waar — het platform gaf er
    // nooit een (de F2F-chatlijst levert alleen "[Image]").
    return (
      <div className="mut2" style={{ fontSize: 12, marginBottom: 4 }} data-testid="media-status">
        {url
          ? `${what} (link expired on ${platform === 'fancentro' ? 'FC' : 'F2F'})`
          : geduld && platform === 'f2f'
            ? `Loading ${what.toLowerCase()} from F2F…`
            : `${what} (no preview from ${platform === 'fancentro' ? 'FC' : 'F2F'})`}
      </div>
    )
  }
  // Een bijlage uit onze eigen map krijgt het token in de query mee; een link
  // van het platform blijft zoals hij is. Zie `chatMediaUrl`.
  // Klein in de lijst zodat één foto de pagina niet uitrekt; klik opent 'm
  // op volledige grootte in een nieuw tabblad.
  const src = chatMediaUrl(url) as string
  // 17-09-2026 (Jordy: "video werkt ook niet"). Hier ging ELKE bijlage in een
  // <img>, ook een video. Die laadt daar nooit, dus sloeg `onError` aan en
  // stond er "Video (link expired on FC)" terwijl de video gewoon verstuurd
  // én afgeleverd was. Een video hoort in een <video>, geluid in een <audio>.
  if (soort === 'video') {
    return <video className="bmedia" src={src} controls preload="metadata"
                  onError={() => setBroken(true)} />
  }
  if (soort === 'audio') {
    return <audio src={src} controls style={{ width: '100%', marginBottom: 4 }}
                  onError={() => setBroken(true)} />
  }
  return <img className="bmedia" src={src} alt={what}
              onClick={() => window.open(src, '_blank', 'noopener')}
              onError={() => setBroken(true)} />
}

/**
 * Het chatvenster op zijn eigen route. 02-10-2026 (Jordy): alleen nog het
 * lezen van de route; het scherm zelf is `ChatView`, zodat de Easy-stand van
 * de Chats-pagina hetzelfde venster in zijn middenkolom kan zetten. Hier
 * verandert er niets: zelfde adres, zelfde `?from=` en `?op=`, zelfde knoppen.
 */
export default function ChatWindow() {
  const { id } = useParams()
  const [zoek] = useSearchParams()
  // 19-10-2026 (Jordy): `?tour=1` = de intro-tour opende deze chat (zie tour).
  return <ChatView id={id} from={zoek.get('from')} op={zoek.get('op')} msg={zoek.get('msg')} tour={zoek.get('tour') === '1'} />
}

export interface ChatViewProps {
  id: string | number | undefined
  /** In de Easy-stand van Chats: geen kruimelpad, en andere gesprekken
   *  openen in hetzelfde venster. */
  embedded?: boolean
  /** `waiting` als het gesprek uit de Waiting queue komt (mark-read-vlag). */
  from?: string | null
  /** Doorklik vanaf Earnings: het tijdstip van de verkoop. */
  op?: string | null
  /** 23-10-2026 (Jordy): `?msg=<id>` vanaf Earnings: direct naar het gekochte bericht. */
  msg?: string | null
  /** Sluiten (embedded); zonder: terug naar /conversations. */
  onClose?: () => void
  /** Een ander gesprek openen (embedded); zonder: naar zijn eigen route. */
  onOpen?: (id: number) => void
  /**
   * 19-10-2026 (Jordy): de intro-tour opende deze chat. HARDE EIS: dan wordt
   * er NIETS als gelezen gemarkeerd en schrijft openen niets: geen mark-read,
   * geen open-label ("Being handled by"), geen refresh-media, en de server
   * slaat bij het ophalen het auto-blok (dat ook last_read_at zet) over.
   */
  tour?: boolean
}

export function ChatView({ id: idProp, embedded = false, from, op, msg, onClose, onOpen, tour = false }: ChatViewProps) {
  const id = idProp == null ? undefined : String(idProp)
  const navigate = useNavigate()
  /** Terug naar het overzicht: in de Easy-stand sluit dit alleen het gesprek. */
  const naarChats = () => (onClose ? onClose() : navigate('/conversations'))
  /** Een ander gesprek van deze fan: in de Easy-stand in hetzelfde venster. */
  const openGesprek = (cid: number) =>
    (onOpen ? onOpen(cid) : navigate(`/conversations/show/${cid}`))
  const { run, busy, notify } = useActions()
  const [draft, setDraft] = useState('')
  /**
   * 18-10-2026 (Jordy): een Standard answer uit Model info in het berichtvak, in
   * de taal van dit gesprek (de server kiest: NL-fan -> Nederlands, anders Engels;
   * vertaling via Mistral). Niets wordt verstuurd; de chatter past aan en verstuurt zelf.
   */
  /**
   * 18-10-2026 (Jordy): mijn mislukte bijlage-opdrachten in dit gesprek, met een
   * Retry-knop (zelfde content, nieuwe opdracht; niets automatisch verzonden).
   */
  const mislukteMedia = useApi<{ id: string; status: string; error: string | null; content_item_ids: number[];
    message_ids?: number[];
    caption: string; is_ppv: boolean; ppv_price: number; item_count: number; platform: string;
    created_at: string | null }[]>(
    id ? `/api/admin/conversations/media-send-jobs/mine?conversation_id=${id}&limit=10` : null, [id])
  usePoll(8000, mislukteMedia.refetch)
  const [mediaRetryBezig, setMediaRetryBezig] = useState<string | null>(null)
  const mediaOpnieuw = async (j: { id: string; content_item_ids: number[]; caption: string; is_ppv: boolean;
                                    ppv_price: number; platform: string }) => {
    setMediaRetryBezig(j.id)
    try {
      await api.post(`/api/admin/conversations/${id}/send-library-content`, {
        content_item_ids: j.content_item_ids,
        // Het bijschrift staat al zoals het naar de fan ging: niet nog eens vertalen.
        caption: j.caption,
        is_ppv: j.is_ppv,
        ppv_price: j.ppv_price,
        client_request_id: `retry-${j.id}-${Date.now()}`,
        ...(j.platform === 'f2f' ? {} : { translation_enabled: false }),
      })
      await api.post(`/api/admin/conversations/media-send-jobs/${j.id}/ack`).catch(() => {})
      notify('Sending the attachment again…')
    } catch (e) {
      notify(`Retry failed: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setMediaRetryBezig(null)
      mislukteMedia.refetch()
    }
  }
  const mediaWeg = async (jid: string) => {
    await api.post(`/api/admin/conversations/media-send-jobs/${jid}/ack`).catch(() => {})
    mislukteMedia.refetch()
  }
  const zetStandaardAntwoord = async (engels: string, origineel: string) => {
    let tekst = engels
    try {
      const r = await api.post<{ tekst: string; taal: string; vertaling_mislukt?: boolean }>(
        `/api/admin/conversations/${id}/standaard-antwoord`, { engels, origineel })
      tekst = r?.tekst || engels
      if (r?.vertaling_mislukt) notify('Translation unavailable: inserted in English.', false)
    } catch {
      notify('Translation unavailable: inserted in English.', false)
    }
    // Het berichtvak is één regel: achter wat er al staat, met een spatie.
    setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, '')} ${tekst}` : tekst))
  }
  // 06-10-2026 (Jordy): de uitgaande vertaalknop. De standaard komt van de
  // server (F2F altijd aan, FanCentro als de fan Nederlands schrijft); een
  // handmatige klik geldt alleen voor dit geopende gesprek en vervalt bij
  // wisselen of verversen. Zie lib/vertaalKnop.ts.
  const [handmatig, setHandmatig] = useState<boolean | Keuze | null>(null)
  // 20-10-2026 (Jordy): klik op de vertaalknop opent een klein menu met alle standen.
  const [vertaalMenu, setVertaalMenu] = useState(false)
  // 21-10-2026 (Jordy): tip vragen (F2F: echte "Request tip"; FC: tekst).
  const [tipOpen, setTipOpen] = useState(false)
  const [tipBedrag, setTipBedrag] = useState('')
  const [tipTekst, setTipTekst] = useState('')
  const [tipBezig, setTipBezig] = useState(false)
  // Op verzoek opgehaalde vertalingen van inkomende fanberichten: {id: tekst}.
  const [vertalingen, setVertalingen] = useState<Record<number, string>>({})
  // 11-10-2026 (Jordy): "Model & Fan info" staat bij het openen van een
  // gesprek ALTIJD open, in Easy en in Pro. "Hide fan panel" verbergt het
  // alleen voor dit geopende gesprek: niets wordt onthouden, en het volgende
  // gesprek begint weer open. In de Easy-stand is dat een nieuw ChatView
  // (`key={chatId}`); in Pro blijft dit venster staan bij een ander id (zelfde
  // route), daarom het effect. Vervangt de Easy-regel van 03-10-2026
  // (standaard dicht, open/dicht onthouden in localStorage).
  const [showFan, setShowFan] = useState(true)
  useEffect(() => { setShowFan(true) }, [id])
  // 19-10-2026 (Jordy): op de telefoon (onder 900px) staan Model en Fan elk
  // achter een eigen knop in de kop, als lade over het gesprek, en bij elk
  // gesprek standaard DICHT. Eigen staat, los van showFan: de desktop verandert niet.
  const mobiel = useMobiel()
  const [mobielPaneel, setMobielPaneel] = useState<MobielPaneel>(null)
  const [mobielActies, setMobielActies] = useState(false)
  // 19-10-2026 (Jordy): mobiele kop inklappen (meer ruimte voor de chat); per apparaat onthouden.
  const [kopIn, setKopIn] = useState<boolean>(() => {
    try { return localStorage.getItem('mchatkop_in') === '1' } catch { return false }
  })
  const zetKopIn = (v: boolean) => {
    setKopIn(v)
    try { localStorage.setItem('mchatkop_in', v ? '1' : '0') } catch { /* privémodus */ }
  }
  useEffect(() => { setMobielPaneel(null); setMobielActies(false) }, [id])
  const fanZichtbaar = mobiel ? mobielPaneel !== null : showFan
  // 03-10-2026 (Jordy): model-info en de notities van het model ter plekke
  // aanpassen, niet meer naar de modelpagina springen. 16-10-2026: inline in
  // het paneel (ModelKop + ModelInfoSectie, links naast Fan info), geen venster meer.
  /** 15-10-2026 (Jordy): Fan info is inline bewerkbaar (FanInfoPaneel); de
   *  notitiebalk bovenaan zet de cursor in "Important notes" via deze teller. */
  const [notitieFocus, setNotitieFocus] = useState(0)
  const naarNotities = () => { setShowFan(true); setMobielPaneel('fan'); setTab('fan'); setNotitieFocus((n) => n + 1) }
  const [gidsOpen, setGidsOpen] = useState(false)
  // Een fan een eigen naam geven; oud heeft daar het potlood naast de fanregel
  // voor. Zonder dit kon een chatter een fan niet hernoemen.
  const [fanNaamOpen, setFanNaamOpen] = useState(false)
  const [kiezerOpen, setKiezerOpen] = useState(false)
  // Eén media-ophaalverzoek per geopende chat: zodra we een F2F-fanfoto zonder
  // lokaal bestand zien, vragen we de worker 'm op te halen (read is dan
  // terecht — de chatter kijkt nu). Verhindert dat elke 1,2s-poll opnieuw vraagt.
  const mediaOpgevraagd = useRef<string | number | null>(null)
  // Eén aankoopscan per geopende FanCentro-chat (EARN-003): staat er een
  // betaalde bijlage van ons nog op slot, dan kijkt de server één keer bij
  // FanCentro of hij inmiddels gekocht is.
  const aankoopGevraagd = useRef<string | number | null>(null)
  // Eén volledige ophaling per nieuw onbeantwoord F2F-fanbericht (zie
  // lib/f2fStaart.ts): "gesprek:bericht" van de laatste aanvraag.
  const staartGevraagd = useRef<string | null>(null)
  // Wanneer per gesprek het laatst gevraagd (limiet: één per minuut).
  const staartLaatst = useRef<Record<string, number>>({})
  // "show it" op een unlockregel: het bericht waar we naartoe springen, en
  // welk bericht even oplicht zodra het in beeld staat.
  const [springDoel, setSpringDoel] = useState<number | null>(null)
  const [uitgelicht, setUitgelicht] = useState<number | null>(null)
  const [springFout, setSpringFout] = useState<number | null>(null)
  const springLengte = useRef<number>(-1)
  const aiStand = useAiStand()
  /** De hoofdschakelaar staat uit; per gesprek instellen mag, maar er gaat
   *  niets uit. */
  const aiGlobaalUit = aiStand.data ? !aiStand.data.effective : false
  const [fanNaam, setFanNaam] = useState('')
  // Oud opent op Fan info; Transactions en History zijn de twee andere tabs.
  // 16-10-2026 (Jordy): Model en Fan in één weergave (geen apart tabblad Model info).
  const [tab, setTab] = useState<'fan' | 'transactions' | 'history'>('fan')
  const [assignOpen, setAssignOpen] = useState(false)
  // 30-09-2026 (Jordy): wat dit account mag. De server weigert de rest al;
  // hier verdwijnen de vinkjes en knoppen die toch niets zouden doen.
  const mij = useMij().data
  const [assignPick, setAssignPick] = useState<number[]>([])
  const [paidNext, setPaidNext] = useState(false)
  const [price, setPrice] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const bottom = useRef<HTMLDivElement | null>(null)

  // Hoeveel berichten er opgehaald worden. Oud begint met een venster en
  // schuift dat op met "Load older messages"; zonder die knop kon een chatter
  // nooit bij de rest van een lang gesprek.
  // 29-09-2026: de route kent `message_limit`, geen `limit`. Die werd stil
  // genegeerd, dus de backend gaf altijd de laatste 100 terug en "Load older
  // messages" en "show it" haalden nooit iets ouders op. Start op 100 (wat er
  // altijd al stond), per stap 80 erbij, tot het maximum van de route.
  const [limiet, setLimiet] = useState(100)
  const [ouderBezig, setOuderBezig] = useState(false)
  const chatScroll = useRef<HTMLDivElement>(null)
  const bovenWacht = useRef<HTMLDivElement>(null)
  const anker = useRef<{ hoogte: number; top: number; aantal: number } | null>(null)
  /** Is het venster al een keer naar het nieuwste bericht gesprongen? */
  const [beneden, setBeneden] = useState(false)

  const q = useApi<Detail>(
    `/api/admin/conversations/${id}?message_limit=${limiet}${tour ? '&tour=1' : ''}`, [id, limiet, tour])
  // Alleen ophalen voor wie mag toewijzen; voor een chatter gaf dit een 403.
  const chatters = useApi<{ chatters: AssignableChatter[] }>(
    magPriveBeheer(mij) ? '/api/admin/conversations/panels/assignable-chatters' : null,
    [magPriveBeheer(mij)])
  // Het oude scherm ververst dit elke ~1,2 s; zo komen nieuwe berichten binnen.
  usePoll(1200, q.refetch)

  const c = q.data?.conversation
  // 18-10-2026 (Jordy): Model info in het Engels (weergavelaag, gecachet per model + gidsversie).
  const modelEngels = useModelInfoEngels(c?.model_persona_id)
  /** 18-10-2026: een betaalde bijlage zonder (geldige) prijs gaat niet weg. */
  const bijlagePrijsFout = file && paidNext ? kiezerPrijsFout(c?.platform, price) : null
  const messages = useMemo(() => q.data?.messages ?? [], [q.data])

  /**
   * Een bundel is ÉÉN bericht.
   *
   * 17-09-2026 (Jordy, browsertest): een gratis bundel van een video en twee
   * foto's kwam als drie losse ADMIN-bubbels in beeld. De aflevering klopte —
   * één opdracht, één platformbericht — maar het scherm tekende elke rij apart.
   * Het oude dashboard vouwde ze samen op `bundle_id`; dat doen we hier weer.
   * De eerste rij draagt de bubbel (met de tekst en de prijs), de bijlagen van
   * de hele bundel hangen eronder.
   */
  const zichtbareBerichten = useMemo(() => {
    const gezien = new Set<string>()
    const uit: Message[] = []
    for (const m of messages) {
      const bundel = String(m.bundle_id || '')
      if (!bundel) { uit.push(m); continue }
      if (gezien.has(bundel)) continue
      gezien.add(bundel)
      const leden = messages.filter((x) => String(x.bundle_id || '') === bundel)
      // 21-10-2026 (Jordy): één aankoop ontgrendelt de hele bundel. Klapte alleen de
      // rij met het platform-id om, dan bleef de bubbel (eerste rij) "Not paid".
      const open = leden.find((x) => x.is_unlocked)
      uit.push({
        ...m,
        ...(open && !m.is_unlocked ? { is_unlocked: true, unlocked_at: open.unlocked_at ?? m.unlocked_at } : {}),
        bundelMedia: leden
          .filter((x) => x.media_url || x.media_type)
          .map((x) => ({ url: x.media_url, type: x.media_type })),
      })
    }
    return uit
  }, [messages])

  /**
   * 17-09-2026 (Jordy: "op het oude dashboard was dit instant"). Het bericht
   * stond hier pas in beeld na de POST én een nieuwe ophaalronde — samen al
   * gauw een seconde, terwijl de server de bubbel al had. Nu zetten we hem
   * METEEN neer, nog vóór de POST, met een "bezig"-stand. Zodra de echte rij
   * uit de server binnenkomt, verdwijnt de tijdelijke.
   */
  const [bezigBubbels, setBezigBubbels] = useState<BezigBubbel[]>([])
  /** 04-10-2026 (Jordy): berichten waarvoor een Resend onderweg is (tegen een
   *  dubbele klik; de server is daarnaast zelf idempotent). */
  const [resendBezig, setResendBezig] = useState<Set<number>>(() => new Set())
  /** 12-10-2026: berichten waarvan de verwijderopdracht nog bij de worker ligt. */
  const [verwijderBezig, setVerwijderBezig] = useState<Set<number>>(() => new Set())
  /** 19-10-2026: telefoon — het bericht waarvoor de bevestiging openstaat. */
  const [teVerwijderen, setTeVerwijderen] = useState<number | null>(null)
  useEffect(() => {
    if (!bezigBubbels.length) return
    const aanwezig = new Set(messages.map((m) => Number(m.id)))
    setBezigBubbels((lijst) => lijst.filter((b) => {
      if (b.serverId && aanwezig.has(b.serverId)) return false
      // Vangnet: zonder server-id verdwijnt hij na een minuut, zodat een
      // mislukte ronde geen bubbel laat hangen die er niet meer is.
      return b.fout != null || Date.now() - b.at < 60_000
    }))
  }, [messages, bezigBubbels.length])

  /** Versturen — vanaf het icoon en vanaf Enter, zodat beide precies hetzelfde
   *  doen. Geen bevestigingspopup meer (verzoek Jordy 10-09): Enter/klik
   *  verstuurt meteen. Tekst én media/PPV gaan via de wachtrij; de worker levert
   *  af en het chatvenster volgt de job-status. */
  const verstuur = () => {
    // 10-09: NIET vooraf blokkeren op messaging_connected. Dat werd in het
    // web-proces berekend, en dat proces bezit onder WEB_ONLY geen luisteraars
    // — dus het meldde altijd "niet verbonden" en hield elk bericht tegen
    // vóórdat het de wachtrij bereikte. De worker bezit de verbinding en levert
    // af; de enige terechte "niet afgeleverd" komt terug als de job-status
    // failed, ná een echte poging.
    if (!file) {
      return verstuurViaWachtrij()
    }
    return verstuurMediaViaWachtrij()
  }

  /**
   * Tekst versturen via de wachtrij: schrijf de opdracht weg, toon 'In de
   * wachtrij', en volg de status tot 'Verstuurd' of 'Mislukt: reden'. De
   * idempotentiesleutel is een verse uuid, zodat een dubbele klik geen tweede
   * opdracht schrijft (de server dedupeert erop).
   */
  async function verstuurViaWachtrij(
    opnieuw?: { key: string; tekst: string; vertaal: boolean },
  ) {
    if (!opnieuw && !draft.trim()) return
    // De bubbel gaat er METEEN in, vóór het netwerk. Het invoerveld is direct
    // leeg, zoals bij oud; blijkt de verzending te mislukken, dan blijft de
    // bubbel staan met de reden erop.
    // 06-10-2026: bij "Retry translation" / "Send as typed" hergebruiken we de
    // bubbel van de mislukte poging, met dezelfde tekst.
    const key = opnieuw?.key ?? `bezig-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const tekst = opnieuw?.tekst ?? draft
    const vertaal = opnieuw ? opnieuw.vertaal : translate
    if (opnieuw) {
      setBezigBubbels((l) => l.map((b) =>
        b.key === key ? { ...b, fout: undefined, vertaal, at: Date.now() } : b))
    } else {
      setBezigBubbels((l) => [...l, { key, tekst, vertaal, at: Date.now() }])
      setDraft(''); setFile(null); setPrice(''); setPaidNext(false)
    }
    try {
      const r = await api.post<{ job_id: number; status: string; message_id?: number; translation_failed?: boolean }>(
        `/api/admin/conversations/${id}/queue-send`,
        {
          content: tekst,
          client_request_id:
            (crypto as any)?.randomUUID?.() ?? `${id}-${Date.now()}-${Math.random()}`,
          // 18-10-2026 (Jordy, DEFINITIEF): aan = automatisch (de server vertaalt
          // alleen voor een fan die Nederlands schrijft); uit = zoals getypt.
          // Geen vaste doeltaal meer: die gaf ook een Engelse fan Nederlands.
          ...(knop.vast ? {} : { translation_enabled: vertaal }),
          // 20-10-2026 (Jordy): handmatig "→ Dutch" = vaste doeltaal.
          ...(!knop.vast && vertaal && knop.doel ? { translate_to: knop.doel } : {}),
        },
      )
      if (r?.message_id) {
        setBezigBubbels((l) => l.map((b) =>
          b.key === key ? { ...b, serverId: Number(r.message_id) } : b))
      }
      q.refetch()
      // 14-10-2026 (Jordy): een mislukte vertaling blokkeert niet meer; de server
      // verstuurde zoals getypt (Engels). Dat zeggen we duidelijk.
      if (r?.translation_failed) notify(`Translation failed: sent in English as typed. ${MELD_VIA_TELEGRAM}`, false)
      if (r?.job_id) volgWachtrij(r.job_id)
    } catch (e) {
      const reden = e instanceof Error ? e.message : String(e)
      setBezigBubbels((l) => l.map((b) => (b.key === key ? { ...b, fout: reden } : b)))
      notify(`Send: ${reden}`, false)
    }
  }

  /** 21-10-2026 (Jordy): een tip-aanvraag via dezelfde wachtrij als tekst. */
  async function vraagTip() {
    const bedrag = Number(tipBedrag)
    if (!(bedrag >= 1 && bedrag <= 500) || tipBezig) return
    setTipBezig(true)
    try {
      const r = await api.post<{ job_id: number; message_id?: number; translation_failed?: boolean }>(
        `/api/admin/conversations/${id}/queue-send`,
        {
          content: tipTekst.trim(),
          tip_request_price: bedrag,
          client_request_id:
            (crypto as any)?.randomUUID?.() ?? `${id}-tip-${Date.now()}-${Math.random()}`,
          ...(knop.vast ? {} : { translation_enabled: translate }),
          ...(!knop.vast && translate && knop.doel ? { translate_to: knop.doel } : {}),
        },
      )
      setTipOpen(false); setTipBedrag(''); setTipTekst('')
      q.refetch()
      notify('Tip request queued.', true)
      if (r?.job_id) volgWachtrij(r.job_id)
    } catch (e) {
      notify(`Tip request: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setTipBezig(false)
    }
  }

  /**
   * Media/PPV versturen via de wachtrij: de upload gaat naar upload-media, dat
   * het bestand lokaal opslaat en (onder WEB_ONLY) een media/PPV-opdracht in de
   * wachtrij zet in plaats van zelf te versturen — dat laatste deed niets omdat
   * het web geen sockets heeft. Krijgen we een job terug, dan volgen we de
   * status net als bij tekst; komt er geen job (worker-proces levert direct),
   * dan is de bubbel al afgeleverd.
   */
  async function verstuurMediaViaWachtrij() {
    if (!file) return
    const key = `bezig-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    // De bijlage tonen we uit het lokale bestand, dus de bubbel staat er met
    // foto of video vóór de upload klaar is.
    const preview = URL.createObjectURL(file)
    const soort = file.type.startsWith('video') ? 'video'
      : file.type.startsWith('audio') ? 'audio' : 'image'
    setBezigBubbels((l) => [
      ...l, { key, tekst: draft, media: preview, mediaSoort: soort, at: Date.now() },
    ])
    setDraft(''); setFile(null); setPrice(''); setPaidNext(false)
    try {
      const r = await sendMessage() as
        { job_id?: number; delivered?: boolean; message_id?: number; id?: number; translation_failed?: boolean } | null
      const mid = Number(r?.message_id ?? r?.id ?? 0)
      if (mid > 0) {
        setBezigBubbels((l) => l.map((b) => (b.key === key ? { ...b, serverId: mid } : b)))
      }
      q.refetch()
      if (r?.translation_failed) notify(`Caption translation failed: sent in English as typed. ${MELD_VIA_TELEGRAM}`, false)
      if (r?.job_id) volgWachtrij(r.job_id)
      else if (r?.delivered) notify('Sent.')
    } catch (e) {
      const reden = e instanceof Error ? e.message : String(e)
      setBezigBubbels((l) => l.map((b) => (b.key === key ? { ...b, fout: reden } : b)))
      notify(`Send: ${reden}`, false)
    }
  }

  /** Trek een eigen verstuurd bericht in. Onder WEB_ONLY zet de server een
   *  opdracht in de wachtrij en trekt de worker het op het platform in; de
   *  1,2s-poll haalt de bubbel weg zodra hij weg is. */
  async function verwijderBericht(mid: number, bevestigd = false) {
    if (verwijderBezig.has(mid)) return
    // 19-10-2026 (Jordy): op de telefoon een bevestiging IN de pagina
    // (components/MobielBevestig): window.confirm wordt daar soms stil
    // overgeslagen en dan gebeurde er niets. Desktop ongewijzigd.
    if (!bevestigd && mobiel) { setTeVerwijderen(mid); return }
    // 14-10-2026 (Jordy): de knop is nu goed zichtbaar; onomkeerbaar, dus eerst bevestigen.
    if (!bevestigd && !window.confirm('Delete this message for the fan? It is unsent on the platform and cannot be undone.')) return
    try {
      const r = await api.del<{ queued?: boolean; ok?: boolean; job_id?: number | null }>(
        `/api/admin/conversations/${id}/messages/${mid}`)
      q.refetch()
      if (r?.queued && r.job_id) {
        void volgVerwijdering(mid, r.job_id)
      } else {
        notify(r?.queued ? 'Delete queued…' : 'Deleted.')
      }
    } catch (e) {
      notify(`Delete: ${e instanceof Error ? e.message : String(e)}`, false)
    }
  }

  /** 12-10-2026 (Jordy: "Delete queued…, maar het bericht wordt niet verwijderd").
   *  De worker trekt in en bevestigt eerst bij het platform; dat duurt soms
   *  tientallen seconden. Tot dan staat er "Deleting…" bij het bericht, en
   *  daarna "Deleted." of de echte reden waarom het niet lukte. */
  async function volgVerwijdering(mid: number, jobId: number) {
    setVerwijderBezig((s) => new Set(s).add(mid))
    notify('Deleting…')
    try {
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => window.setTimeout(res, 1000))
        try {
          const s = await api.get<{ status: string; error?: string | null }>(
            `/api/admin/conversations/delete-jobs/${jobId}/status`,
          )
          if (s?.status === 'done') { notify('Deleted.'); q.refetch(); return }
          if (s?.status === 'failed') {
            notify(`Not deleted: ${s.error ?? 'unknown reason'}`, false); q.refetch(); return
          }
        } catch { /* stand nog niet leesbaar; volgende ronde */ }
      }
      notify('The delete is taking longer than expected. Check the chat in a minute.', false)
    } finally {
      setVerwijderBezig((s) => { const n = new Set(s); n.delete(mid); return n })
    }
  }

  /**
   * 04-10-2026 (Jordy): een aantoonbaar onverzonden bericht opnieuw in de
   * wachtrij. De server hergebruikt dezelfde berichtrij (geen tweede bubbel) en
   * geeft bij een tweede klik de bestaande opdracht terug.
   */
  async function verstuurOpnieuw(mid: number) {
    if (resendBezig.has(mid)) return
    setResendBezig((s) => new Set(s).add(mid))
    try {
      const r = await api.post<{ job_id: number; status: string; already_resent?: boolean }>(
        `/api/admin/conversations/${id}/messages/${mid}/resend`, {})
      notify(r?.already_resent ? 'Already resent — following that send.' : 'Resend queued…')
      q.refetch()
      if (r?.job_id) await volgWachtrij(r.job_id)
    } catch (e) {
      notify(`Resend: ${e instanceof Error ? e.message : String(e)}`, false)
      q.refetch()
    } finally {
      setResendBezig((s) => { const n = new Set(s); n.delete(mid); return n })
    }
  }

  /** Poll de status van één verzendopdracht tot hij klaar is. */
  async function volgWachtrij(jobId: number) {
    for (let i = 0; i < 15; i++) {
      await new Promise((res) => window.setTimeout(res, 700))
      try {
        const s = await api.get<{ status: string; error?: string | null }>(
          `/api/admin/conversations/queue-send/${jobId}/status`,
        )
        if (s?.status === 'sent') { notify('Sent.'); q.refetch(); return }
        if (s?.status === 'failed') {
          notify(`Failed: ${s.error ?? 'unknown reason'}`, false); q.refetch(); return
        }
        if (s?.status === 'stuck') {
          notify('Stuck — check manually, not resent.', false)
          return
        }
      } catch { /* status nog niet leesbaar; volgende ronde */ }
    }
  }

  /**
   * Haalt het volgende blok oudere berichten erbij.
   *
   * 30-09-2026 (Jordy): geen knop meer — dit gaat vanzelf zodra je naar boven
   * scrolt. De scrollhoogte wordt vooraf onthouden, zodat het bericht waar je
   * naar keek na het toevoegen op dezelfde plek blijft staan in plaats van dat
   * het venster verspringt.
   */
  function laadOuder() {
    if (ouderBezig || limiet >= MAX_BERICHTEN) return
    const el = chatScroll.current
    anker.current = el ? { hoogte: el.scrollHeight, top: el.scrollTop, aantal: messages.length } : null
    setOuderBezig(true)
    setLimiet((n) => Math.min(n + 80, MAX_BERICHTEN))
    // Vangnet: komt er niets bij (geen oudere berichten), dan niet blijven hangen.
    window.setTimeout(() => setOuderBezig(false), 4000)
  }

  // Na het toevoegen van oudere berichten: dezelfde plek vasthouden.
  useLayoutEffect(() => {
    const a = anker.current
    const el = chatScroll.current
    if (!a || !el || messages.length <= a.aantal) return
    el.scrollTop = a.top + (el.scrollHeight - a.hoogte)
    anker.current = null
    setOuderBezig(false)
  }, [messages.length])

  // Vanzelf ouder laden bij de bovenkant — pas nadat het venster één keer naar
  // het nieuwste bericht is gesprongen, anders laadt het bij openen meteen alles.
  useBijRand(bovenWacht, chatScroll, laadOuder,
             beneden && !!q.data?.has_more && limiet < MAX_BERICHTEN && !ouderBezig,
             '150px 0px')

  /**
   * Of de AI voor deze chat aanstaat. Oud leidt dat af uit drie velden
   * tegelijk; met alleen `is_excluded_from_ai` las de knop "Pause AI" bij een
   * chat waar de fan zich juist had afgemeld.
   */
  // De knop: standaard van de server, tenzij de chatter hem hier omzette.
  const knop = knopStand(c?.outgoing_translation_default ?? terugval(c?.platform), handmatig)
  const translate = knop.aan
  // Een ander gesprek begint weer op de standaard.
  useEffect(() => { setHandmatig(null) }, [id])
  useEffect(() => { setVertaalMenu(false) }, [id])

  /**
   * Inkomend vertalen zoals oud: staat de fan in een vreemde taal, vraag dan
   * per fanbericht dat nog geen vertaling heeft de Engelse vertaling op. Eén
   * aanroep per bekeken bericht, en alleen als er iets te vertalen valt —
   * goedkoop. Dit staat LOS van de AI en van de uitgaande toggle.
   */
  /**
   * 17-09-2026 (Jordy: "de chatter kan niet lezen wat hiervoor gezegd is").
   * Hier stond een lus die ALLEEN fanberichten vertaalde, en alleen als de
   * fan-taal vreemd was. Een bericht dat rechtstreeks op het platform is
   * getypt — bijvoorbeeld vanuit het oude dashboard, in het Spaans of
   * Nederlands — bleef daardoor onvertaald in beeld staan.
   *
   * Nu gaat alles wat nog geen vertaling heeft in één verzoek naar de server.
   * Die bepaalt de taal offline: Engelse tekst kost geen vertaalaanroep en
   * wordt als `en` onthouden, zodat we er niet telkens op terugkomen.
   */
  // 14-10-2026 (Jordy, KOSTENLEK): berichten waarvoor een vertaalverzoek nog loopt.
  // Vroeger brak elke poll (1,2 s) het lopende verzoek af en startte een nieuw;
  // de server vertaalde dan hetzelfde bericht 5-6 keer tegelijk, en eindeloos als
  // er niets werd opgeslagen.
  const vertaalOnderweg = useRef<Set<number>>(new Set())
  const gemonteerd = useRef(true)
  useEffect(() => () => { gemonteerd.current = false }, [])
  useEffect(() => {
    const teDoen = messages
      .filter((m) => (m.content || '').trim()
        // 18-10-2026 (Jordy): alleen fanberichten. Uitgaande berichten krijgen hun
        // Engelse regel van de server bij het ophalen (src/services/engels_uitgaand.py).
        && String(m.sender).toLowerCase() === 'fan'
        // "" = al verwerkt zonder Engelse regel (de vertaler gaf hetzelfde terug).
        && m.content_english == null
        && !(String(m.sender).toLowerCase() === 'fan' && m.content_translation)
        && String(m.detected_language || '').toLowerCase() !== 'en'
        && vertalingen[m.id] === undefined
        && !vertaalOnderweg.current.has(Number(m.id)))
      .slice(0, 60)
    if (!teDoen.length) return
    for (const m of teDoen) vertaalOnderweg.current.add(Number(m.id))
    ;(async () => {
      let uit: Record<string, string | null> = {}
      try {
        const r = await api.post<{ translations: Record<string, string | null> }>(
          `/api/admin/conversations/${id}/messages/translate-batch`,
          { message_ids: teDoen.map((m) => m.id) },
        )
        uit = r?.translations || {}
      } catch {
        // Dienstfout of geen sleutel: markeer als behandeld zodat het scherm
        // niet blijft proberen; de bubbel toont gewoon het origineel.
        uit = {}
      } finally {
        for (const m of teDoen) vertaalOnderweg.current.delete(Number(m.id))
      }
      // Het resultaat altijd vastleggen (niet meer weggooien bij een nieuwe poll),
      // zodat hetzelfde bericht in deze sessie nooit opnieuw wordt aangevraagd.
      if (!gemonteerd.current) return
      setVertalingen((v) => {
        const kopie = { ...v }
        for (const m of teDoen) kopie[m.id] = uit[String(m.id)] ?? ''
        return kopie
      })
    })()
  }, [messages, id, vertalingen])

  /** De lokale tijd bij de fan, als zijn tijdzone bekend is; anders null.
   *  18-10-2026 (Jordy): het paneel toont dan "—" met een kleine hint
   *  ("set location or timezone") in plaats van de lange zin. */
  const fanLokaleTijd = (() => {
    const zone = c?.fan_timezone
    if (!zone) return null
    try {
      return new Intl.DateTimeFormat('nl-NL', {
        timeZone: String(zone), hour: '2-digit', minute: '2-digit',
      }).format(new Date())
    } catch {
      return null
    }
  })()

  /** De statusregel in de fan-kaart, zoals oud hem opbouwt: online, anders
   *  offline met het laatste moment erbij, en "Unknown" als dat er niet is. */
  const fanStatus = (() => {
    if (c?.fan_online) return 'Online'
    if (c?.fan_last_active_at) return `Offline · last seen ${dtSec(c.fan_last_active_at)}`
    return 'Unknown'
  })()

  /** Wat deze fan aan fooien heeft uitgegeven; oud toont dat apart.
   *  15-10-2026: las de sleutels "tip" en "Tips" uit `fan_spend_mix`, maar
   *  die heet `tips`, dus hier stond altijd €0. Nu zoals oud: `fan_spend_by_category.tip`,
   *  dan `fan_spend_mix.tips`, dan `fan_spend_breakdown`; null = niets bekend. */
  const fooien = c ? fooienVan(c) : null

  const aiActief =
    String(c?.status ?? '').toLowerCase() === 'active' &&
    !c?.is_excluded_from_ai &&
    c?.ai_opted_in !== false

  // Naar beneden als er ONDERAAN iets bijkomt. Oudere berichten erbij laden
  // (ook voor "show it") verandert het laatste bericht niet, dus dat trekt
  // de chatter niet meer terug naar onderen.
  const laatsteId = messages.length ? messages[messages.length - 1].id : null
  /** 19-10-2026 (Jordy): ALLEEN mobiel — de chat zelf naar de bodem (scrollIntoView
   *  schoof op de telefoon soms de hele pagina in plaats van de chat). */
  const mobielNaarBodem = () => {
    const el = chatScroll.current
    if (el) el.scrollTop = el.scrollHeight
  }
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
    if (mobiel) mobielNaarBodem()
    // Pas vanaf nu mag "bij de bovenkant" oudere berichten laden.
    if (laatsteId != null) window.setTimeout(() => setBeneden(true), 300)
  }, [laatsteId])

  // 19-10-2026 (Jordy): ALLEEN mobiel — opent het toetsenbord (het zichtbare deel van
  // het scherm krimpt) terwijl het invoerveld focus heeft: naar de bodem, zodat het
  // laatste bericht en het invoerveld boven het toetsenbord blijven.
  useEffect(() => {
    if (!mobiel || typeof window === 'undefined' || !window.visualViewport) return
    const vv = window.visualViewport
    const bij = () => {
      const a = document.activeElement
      if (a instanceof HTMLElement && a.classList.contains('veld')) mobielNaarBodem()
    }
    vv.addEventListener('resize', bij)
    return () => vv.removeEventListener('resize', bij)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobiel])

  // "show it": laad oudere pagina's tot het vrijgekochte bericht er is, en
  // scroll er dan naartoe. Zoals oud (revealMessage).
  useEffect(() => {
    if (springDoel == null) return
    const gevonden = messages.find((m) => m.id === springDoel && !m.event_type)
    if (gevonden) {
      // 23-10-2026 (Jordy): een bundellid staat in beeld als de eerste rij van de bundel.
      const kop = gevonden.bundle_id
        ? zichtbareBerichten.find((x) => !x.event_type && x.bundle_id === gevonden.bundle_id)
        : undefined
      const doel = kop ? kop.id : springDoel
      window.setTimeout(() => {
        const node = document.querySelector(`[data-msg-id="${doel}"]`)
        node?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        setUitgelicht(doel)
        window.setTimeout(() => setUitgelicht((u) => (u === doel ? null : u)), 2000)
      }, 60)
      setSpringDoel(null)
      return
    }
    if (q.data?.has_more) {
      // Pas een volgend blok vragen als het vorige binnen is; de 1,2s-poll
      // levert tussendoor dezelfde lengte op en mag geen extra blok vragen.
      if (limiet < MAX_BERICHTEN) {
        if (springLengte.current === messages.length) return
        springLengte.current = messages.length
        setLimiet((n) => Math.min(n + 80, MAX_BERICHTEN))
        return
      }
    }
    setSpringFout(springDoel)
    setSpringDoel(null)
  }, [springDoel, messages, q.data?.has_more, limiet])

  // Doorklik vanaf Earnings (29-09-2026): `?op=<tijdstip van de verkoop>`.
  // Laad terug tot dat moment en spring naar het laatste bericht op of vlak
  // vóór de verkoop, met hetzelfde springDoel als "show it". Is er geen
  // bericht vóór, dan het eerste erna.
  // 23-10-2026 (Jordy): `?msg=` (het gekochte bericht) gaat voor `?op=`.
  const opTijd = msg ? null : (op ?? null)
  const msgGedaan = useRef<string | null>(null)
  useEffect(() => {
    const sleutel = `${id}|${msg}`
    if (!msg || msgGedaan.current === sleutel || !messages.length) return
    msgGedaan.current = sleutel
    setSpringFout(null)
    springLengte.current = -1
    setSpringDoel(Number(msg))
  }, [msg, id, messages.length])

  // 02-10-2026 (Jordy): een chat uit de Waiting queue openen zonder te
  // antwoorden verplaatst hem naar Unanswered — zoals oud. Het overzicht leidt
  // dat af uit last_dashboard_opened_at / last_waiting_queue_opened_at, maar
  // v2 riep mark-read nooit aan. Bij openen één keer (met de wachtrij-vlag als
  // we daarvandaan komen) en daarna elke 5 s zolang het tabblad zichtbaar is,
  // zodat wat binnenkomt terwijl de chat open staat ook als gezien telt.
  const vanWachtrij = from === 'waiting'
  useEffect(() => {
    if (!id || tour) return  // tour-modus: NOOIT mark-read (zie ChatViewProps.tour)
    const meld = (eerste: boolean) => {
      if (!eerste && document.visibilityState !== 'visible') return
      const vlag = eerste && vanWachtrij
      api.post(`/api/admin/conversations/${id}/mark-read?from_waiting_queue=${vlag}`).catch(() => {})
    }
    meld(true)
    const t = window.setInterval(() => meld(false), 5000)
    return () => window.clearInterval(t)
  }, [id, vanWachtrij, tour])
  // 21-10-2026 (Jordy): openen = claimen (lib/inboxOpen.ts); van een ander = alleen lezen.
  // Niet in de tour.
  const inbox = useInboxOpen(tour ? null : id)
  const opGedaan = useRef<string | null>(null)
  const opLengte = useRef<number>(-1)
  useEffect(() => {
    const sleutel = `${id}|${opTijd}`
    if (!opTijd || opGedaan.current === sleutel) return
    const doelMs = new Date(opTijd).getTime()
    const echt = messages.filter((m) => !m.event_type && m.created_at)
    if (!Number.isFinite(doelMs) || !echt.length) return
    const tijd = (m: Message) => new Date(tijdUtc(m.created_at)).getTime()
    if (tijd(echt[0]) > doelMs && q.data?.has_more && limiet < MAX_BERICHTEN) {
      if (opLengte.current === messages.length) return
      opLengte.current = messages.length
      setLimiet((n) => Math.min(n + 80, MAX_BERICHTEN))
      return
    }
    let doel: number | null = null
    for (const m of echt) if (tijd(m) <= doelMs + 5000) doel = m.id
    opGedaan.current = sleutel
    setSpringDoel(doel ?? echt[0].id)
  }, [opTijd, id, messages, q.data?.has_more, limiet])

  // F2F-fanfoto's komen binnen zonder lokaal bestand (de stille lijst-scrape
  // draagt het asset niet). Zodra een chatter deze chat opent en er staat zo'n
  // foto zonder lokaal bestand, vragen we de worker die op te halen — één keer
  // per chat. De 1,2s-poll toont de foto zodra hij lokaal staat.
  useEffect(() => {
    if (tour) return  // tour-modus: geen refresh-media (zie ChatViewProps.tour)
    if (!c || c.platform !== 'f2f') return
    if (mediaOpgevraagd.current === id) return
    const mist = messages.some(
      (m) => String(m.sender).toLowerCase() === 'fan'
        && (m.media_type || m.media_url)
        && !(m.media_url || '').startsWith('/api/admin/conversations/media/'),
    )
    if (!mist) return
    mediaOpgevraagd.current = id ?? null
    api.post(`/api/admin/conversations/${id}/refresh-media`).catch(() => {
      // Mislukt stilletjes; de volgende keer dat de chat opent proberen we weer.
      mediaOpgevraagd.current = null
    })
  }, [c, id, messages])

  // 30-10-2026 (Jordy, fan T84T): twee F2F-fanberichten vlak na elkaar — de
  // chatlijst gaf alleen het laatste, de chatter antwoordde zonder het eerste
  // te zien. Staat het nieuwste bericht als onbeantwoord fanbericht open, dan
  // vragen we de worker het hele gesprek op te halen; één keer per nieuw
  // fanbericht en alleen als het tabblad zichtbaar is. De 1,2s-poll toont wat
  // er bijkomt.
  // 31-10-2026 (Jordy): de fan ziet dan "gelezen", dus ALLEEN als een chatter
  // dit gesprek actief opent om te antwoorden: het is van hem (claim bij openen,
  // zijn antwoord, of privé aan hem). Inzage door owner/manager ('view') of
  // meekijken bij een ander ('other') vraagt niets. De server controleert
  // hetzelfde en houdt de limieten bij (f2f-full-fetch).
  const antwoordt = magStaartVragen(mij?.role, inbox.stand, !!c?.claimed_by_me)
  useEffect(() => {
    if (tour) return  // tour-modus: geen volledige ophaling (zie ChatViewProps.tour)
    if (!antwoordt) return
    const sleutel = staartSleutel(c?.platform, messages)
    if (!sleutel || id == null) return
    const k = `${id}:${sleutel}`
    if (staartGevraagd.current === k) return
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    // Te kort na de vorige: niet als gevraagd markeren, de 1,2s-poll probeert
    // het daarna vanzelf opnieuw.
    if (!staartMagNu(staartLaatst.current[String(id)], Date.now())) return
    staartGevraagd.current = k
    staartLaatst.current[String(id)] = Date.now()
    api.post(`/api/admin/conversations/${id}/f2f-full-fetch`).then((r: any) => {
      // Druk op de server: na de rust van een minuut opnieuw proberen.
      if (r?.data?.reason === 'rate_limited') staartGevraagd.current = null
    }).catch(() => {
      staartGevraagd.current = null
    })
  }, [c, id, messages, antwoordt])

  // FanCentro (EARN-003): staat er een betaalde bijlage van ons op slot, dan
  // vraagt hetzelfde endpoint één keer per geopende chat de aankoopscan. De
  // server kijkt bij FanCentro of die bijlage gekocht is en zet hem dan alsnog
  // op Unlocked, met één omzetregel. De 1,2s-poll toont het resultaat.
  useEffect(() => {
    if (tour) return  // tour-modus: geen refresh-media (zie ChatViewProps.tour)
    if (!c || c.platform !== 'fancentro') return
    if (aankoopGevraagd.current === id) return
    const opSlot = messages.some(
      (m) => !m.event_type
        && String(m.sender).toLowerCase() !== 'fan'
        && (m.is_paid || Number(m.price || 0) > 0)
        && !m.is_unlocked
        && !!m.platform_message_id,
    )
    if (!opSlot) return
    aankoopGevraagd.current = id ?? null
    api.post(`/api/admin/conversations/${id}/refresh-media`).catch(() => {
      aankoopGevraagd.current = null
    })
  }, [c, id, messages])

  /**
   * Verzenden zoals oud: met een bijlage gaat het bestand eerst naar
   * `upload-media` (met de prijs als het een PPV is), anders is het een
   * gewone `send`. `translation_enabled` zegt de server expliciet of de
   * tekst vertaald moet worden.
   */
  async function sendMessage() {
    if (file) {
      const fd = new FormData()
      fd.append('file', file)
      if (draft.trim()) fd.append('caption', draft)
      // De server verwacht is_ppv/ppv_price; een betaalde upload met de oude
      // velden (is_paid/price) ging als GRATIS weg. Nu correct als PPV.
      fd.append('is_ppv', paidNext ? 'true' : 'false')
      if (paidNext && price) fd.append('ppv_price', price)
      // Vertaal het onderschrift net als een tekstbericht, naar de taal van de
      // fan; leeg laten valt op de serververtaling terug.
      // 06-10-2026: aan = altijd naar het Nederlands, expliciet meegegeven.
      // 15-10-2026: F2F (knop vast): geen velden, de server beslist.
      if (!knop.vast) {
        fd.append('translation_enabled', translate ? 'true' : 'false')
        if (knop.doel) fd.append('translate_to', knop.doel)
      }
      // Idempotentie: een dubbele klik schrijft geen tweede opdracht/bericht.
      fd.append('client_request_id',
        (crypto as any)?.randomUUID?.() ?? `${id}-${Date.now()}-${Math.random()}`)
      const r = await fetch(`/api/admin/conversations/${id}/upload-media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_access_token') || ''}` },
        body: fd,
      })
      if (!r.ok) {
        throw new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`)
      }
      return r.json().catch(() => null)
    }
    return api.post(`/api/admin/conversations/${id}/send`, {
      content: draft,
      sender: 'human',
      ...(knop.vast ? {} : { translation_enabled: translate, ...(knop.doel ? { translate_to: knop.doel } : {}) }),
    })
  }

  const post = (path: string, body?: unknown, label?: string, ok?: string) =>
    run({
      label: label ?? path,
      request: mut.post(`/api/admin/conversations/${id}/${path}`, body),
      onDone: q.refetch,
      successText: ok ?? 'Done.',
    })

  if (q.loading && !q.data) return <Loading />
  if (q.error) return <ErrorNote error={q.error} />
  if (!c) return <ErrorNote error="Conversation not found." />

  const fan = c.fan_display_name || c.fan_username || c.fan_id
  // Zoals oud: achter de naam de username tussen haakjes ("Luuk
  // (@user-eed96b8b42bd)"), maar alleen als die er is en van de naam verschilt.
  const fanAt = c.fan_username && c.fan_username !== fan ? ` (@${c.fan_username})` : ''
  const verbonden = q.data?.messaging_connected !== false
  // 04-10-2026 (Jordy): alleen een gemeten storing ('down') kleurt rood en
  // geeft de balk; zonder meting ('unknown') blijft het scherm rustig.
  const verbindingWeg = q.data?.messaging_status === 'down'
  const opnieuwVerstuurd = new Set((q.data?.resent_message_ids ?? []).map(Number))
  /** Mag dit mislukte bericht met één klik opnieuw? Alleen eigen tekst, nooit
   *  vertrokken (de server controleert hetzelfde nog eens), en nog niet eerder
   *  opnieuw verstuurd. Media en Mass DM: geen knop. */
  const kanOpnieuw = (m: Message): boolean =>
    String(m.sender).toLowerCase() === 'human'
    && !m.delivered && !m.platform_message_id
    && isAantoonbaarOnverzonden(m.delivery_error)
    && !opnieuwVerstuurd.has(Number(m.id))
    && !m.media_url && !m.media_type && !m.is_paid && !m.bundle_id
    && !m.mass_message_log_id
    && !!String(m.content ?? '').trim()
  const platformNaam = c.platform === 'fancentro' ? 'FanCentro' : 'F2F'
  /** "door Banki op 05-09, 14:12" — staat in de tooltip van de notitiebalk,
   *  zodat een chatter ziet van wie de waarschuwing komt. */
  const herkomst = q.data?.notes_by
    ? `Last changed by ${q.data.notes_by}`
      + (q.data?.notes_at ? ` on ${dtSec(q.data.notes_at)}` : '')
    : ''


  /** De link naar het profiel op het platform, precies zoals oud hem bouwt:
   *  FanCentro op de slug zonder `fc_`, F2F via de agency-pagina. Nieuw had
   *  alleen de FanCentro-variant, waardoor de knop op elke F2F-chat ontbrak. */
  const modelLink = c.platform === 'fancentro'
    ? (c.model_slug ? `https://fancentro.com/${c.model_slug.replace(/^fc_/, '')}` : null)
    : (c.model_slug
        ? `https://f2f.com/agency/creators/${c.model_slug.replace(/^f2f_/, '')}`
        : null)

  return (
    // Oud omsluit dit hele scherm met één kader van 1632x975 — kruimelpad,
    // kopblok, berichten en fan-paneel zitten daarbinnen. Zonder dat kader
    // zweven de panelen los op de pagina en is niet te zien dat ze bij
    // elkaar horen.
    <div className={['chatwrap', mobiel && mobielActies ? 'm-acties' : '', mobiel && kopIn ? 'm-kop-in' : '']
      .filter(Boolean).join(' ')}>
      {mobiel ? (
        <MobielChatKop model={c.stage_name ?? c.model_slug ?? ''} avatar={c.avatar_url} platform={c.platform}
                       fan={String(fan ?? '')} wachtSinds={c.last_fan_message_at}
                       uitgegeven={c.fan_total_spend}
                       paneel={mobielPaneel}
                       onPaneel={(p) => { setMobielPaneel(p); if (p) setTab('fan') }}
                       acties={mobielActies} onActies={setMobielActies}
                       ingeklapt={kopIn} onKlap={zetKopIn}
                       onTerug={naarChats} />
      ) : null}
      {/* Kopblok: twee regels en klaar. Elke pixel hier gaat af van de
          berichten eronder, en het chatvenster moet in één scherm passen. */}
      <div className="panel chatkop">
        <div className="body">
          {/* Regel 1: wie, waar, hoeveel — plus de fan met zijn wachttijd. */}
          <div className="row kopregel">
            <Avatar name={c.stage_name} src={c.avatar_url} size={26} />
            <b style={{ fontSize: 13 }}>{c.stage_name}</b>
            <Plat value={c.platform} />
            {/* De verbindingsstatus als bolletje naast het platform. Een volle
                rode balk stond hier de hele dag te schreeuwen over iets wat je
                pas merkt op het moment dat je verstuurt; daar hoort de melding
                ook thuis. */}
            <span className={verbindingWeg ? 'verbindstip weg'
                               : verbonden ? 'verbindstip aan' : 'verbindstip uit'}
                  title={verbindingWeg
                    ? (c.platform !== 'fancentro' && !isVolledigBeheer(mij)
                        // 31-10-2026 (Jordy): bij F2F geen tijdstip voor andere rollen.
                        ? 'F2F sync is delayed'
                        : `${platformNaam} connection is down since `
                          + sindsTijd(q.data?.messaging_stale_since))
                    : verbonden
                      ? `${platformNaam} messaging is connected`
                      : `${platformNaam} messaging is not connected for this model — `
                        + 'a message will not go out'} />
            {c.current_tag ? <Tag kind="warn">{c.current_tag}</Tag> : null}
            <Tag kind="mut">{num(c.total_messages)} msgs</Tag>
            {c.fan_online ? <Tag kind="ok">Online</Tag> : null}
            {/* Wat deze fan heeft uitgegeven hoort op de eerste regel: een
                chatter moet dat zien vóór hij begint te typen, niet na het
                scrollen. De uitsplitsing per soort staat in het fan-paneel. */}
            {/* 23-10-2026 (Jordy): nieuwe betaalde abonnee, nog niet persoonlijk bedankt. */}
            <NieuweAbonneeTag c={c as unknown as QueueRow} />
            {/* 19-10-2026 (Jordy): spender = betaald bij WELK model dan ook. */}
            {isSpender(c) ? (
              <span data-testid="spender-badge">
                <Tag kind="warn" title={
                  `${eur(c.fan_total_spend)} with this model · ${eur(alleModellen(c))} with all models`
                }>
                  {c.fan_total_spend > 0 ? `★ ${eur(c.fan_total_spend)}` : `★ Spender · ${eur(alleModellen(c))} other models`}
                </Tag>
              </span>
            ) : null}
            <span className="mut2 kopfan">
              {fan}{fanAt ? <span className="mut2">{fanAt}</span> : null}{' '}
              {c.last_fan_message_at ? (
                <><Wacht sinds={c.last_fan_message_at} />{' '}</>
              ) : null}
              <button className="linkbtn" type="button" title="Rename this fan"
                      onClick={() => { setFanNaam(c.fan_display_name ?? '')
                                       setFanNaamOpen(true) }}>
                ✎
              </button>
              {' · '}{c.platform}
              {c.created_at ? ` · created ${dtSec(c.created_at)}` : ''}
            </span>
            <span className="spacer" style={{ flex: 1 }} />
            {/* 03-10-2026 (Jordy): in de Easy-stand een kruisje rechtsboven;
                het midden gaat terug naar "Select a chat on the left". */}
            {/* 03-10-2026 (Jordy): sluiten is een kruisje in de hoek, in beide
                standen; de tekstknop "Close chat"/"Chats" is weg. */}
            {embedded ? null : (
              <>
                <a className="mut2 kruimel" href="/conversations"
                   onClick={(e) => { e.preventDefault(); naarChats() }}>
                  Chats /
                </a>{' '}
                <span className="mut2 kruimel">Show</span>
              </>
            )}
            <button className="btn ghost sm chatsluit" type="button"
                    aria-label="Close chat" title="Close chat" onClick={naarChats}>
              ✕
            </button>
          </div>

          {/* Oud toont hier wat deze fan heeft uitgegeven: één badge met het
              totaal en daarna de vijf grootste soorten, zodat een chatter in
              één blik ziet waar het geld vandaan komt. */}


          {/* Regel 2: de schakelaars links, de knoppen rechts. */}
          <div className="row kopregel">
            {magTimewaster(mij) ? (
            <label className="chkrow">
              <span className={c.is_timewaster ? 'chk on' : 'chk'}
                    onClick={() => post('timewaster', { is_timewaster: !c.is_timewaster },
                                        'Timewaster', 'Saved.')}>
                {c.is_timewaster ? '✓' : ''}
              </span>
              Timewaster
            </label>
            ) : null}
            {/* 21-10-2026 (Jordy): "Maybe timewaster" naast Timewaster; de ene zet de andere uit. */}
            {magTimewaster(mij) ? (
            <label className="chkrow" data-testid="chk-maybe-timewaster">
              <span className={c.is_maybe_timewaster ? 'chk on' : 'chk'}
                    onClick={() => post('maybe-timewaster', { is_maybe_timewaster: !c.is_maybe_timewaster },
                                        'Maybe timewaster', 'Saved.')}>
                {c.is_maybe_timewaster ? '✓' : ''}
              </span>
              Maybe timewaster
            </label>
            ) : null}
            {magVip(mij) ? (
            /* 21-10-2026 (Jordy): een fan die iets uitgaf is automatisch VIP (live
               regel); het vinkje staat dan aan en is niet uit te zetten. Het
               handmatige vinkje blijft voor niet-spenders. */
            <label className="chkrow" data-testid="chk-vip"
                   title={!c.is_vip && alleModellen(c) > 0 ? `VIP automatically: this fan spent ${eur(alleModellen(c))}` : undefined}>
              <span className={c.is_vip || alleModellen(c) > 0 ? 'chk on' : 'chk'}
                    onClick={() => { if (c.is_vip || !(alleModellen(c) > 0)) post('vip', { is_vip: !c.is_vip }, 'VIP', 'Saved.') }}>
                {c.is_vip || alleModellen(c) > 0 ? '✓' : ''}
              </span>
              VIP
            </label>
            ) : null}
            {/* Je eigen privé-chat vrijgeven mag altijd (de server staat het
                toe); zelf privé maken alleen met het recht. */}
            {c.claimed_by_me || magZelfPrive(mij) ? (
            <label className="chkrow">
              <span className={c.claimed_by_me ? 'chk on' : 'chk'}
                    onClick={() => post(c.claimed_by_me ? 'release' : 'claim', undefined,
                                        'Private', 'Saved.')}>
                {c.claimed_by_me ? '✓' : ''}
              </span>
              Private to me
            </label>
            ) : null}

            <span className="mut2" style={{ fontSize: 12 }}>
              Chatter: {c.claimed_by_name ?? 'unassigned'}
            </span>
            {magPriveBeheer(mij) ? (
            <button className="btn ghost sm" type="button"
                    onClick={() => { setAssignPick(c.claimed_admin_ids ?? []); setAssignOpen(true) }}>
              Assign to chatter
            </button>
            ) : null}

            <div className="spacer" style={{ flex: 1 }} />
            {/* Volgorde zoals oud: het platform eerst, dan terug naar de
                inbox, dan synchroniseren en blokkeren. */}
            {/* 03-10-2026 (Jordy): "Model on FC/F2F" alleen voor beheer; de
                Sync-knop is weg (werd niet gebruikt; bij openen haalt het
                venster zelf al op wat nodig is); sluiten is het kruisje. */}
            {modelLink && isBeheer(mij) ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => window.open(modelLink, '_blank')}>
                Model on {c.platform === 'fancentro' ? 'FC' : 'F2F'}
              </button>
            ) : null}
            {magBlokkeren(mij) ? (
            <button className="btn ghost sm" type="button"
                    disabled={busy === 'Block'}
                    onClick={() => run({
                      label: 'Block',
                      confirm: c.dashboard_blocked
                        ? 'Unblock this chat?'
                        : 'Block this chat on the dashboard?',
                      request: mut.post(
                        `/api/admin/conversations/${id}/${c.dashboard_blocked ? 'unblock' : 'block'}`),
                      onDone: q.refetch,
                      successText: c.dashboard_blocked ? 'Unblocked.' : 'Blocked.',
                    })}>
              {c.dashboard_blocked ? 'Unblock' : 'Block'}
            </button>
            ) : null}
          
            {/* Oud heeft ze allebei tegelijk: "Fan info" in de kop en "Hide
                fan panel" boven het paneel. */}
            <button className="btn ghost sm" type="button"
                    onClick={() => setShowFan(!showFan)}>
              Model &amp; Fan info
            </button>
            {/* Oud leidt deze knop uit DRIE velden af, niet uit één:
                de chat moet actief zijn, niet van de AI uitgesloten, en de fan
                moet niet expliciet hebben afgemeld. Nieuw keek alleen naar
                `is_excluded_from_ai` en las daardoor "Pause AI" bij een chat
                waar de AI juist uit stond (ai_opted_in = false). */}
            {/* De knop blijft werken als de hoofdschakelaar uit staat — je
                mag een gesprek alvast klaarzetten — maar hij zegt er dan bij
                dat er niets gaat gebeuren. Een knop die stil niets doet is
                erger dan een knop die uitlegt waarom. */}
            {/* 03-10-2026 (Jordy): AI aan/uit alleen voor wie dat mag
                (beheer, of een chatter met het recht toggle_ai). */}
            {magAiSchakelen(mij) ? (
            <button className="btn ghost sm" type="button"
                    disabled={busy === 'AI'}
                    title={aiGlobaalUit
                      ? 'AI chatbot is globally off — this chat is ready, '
                        + 'but nothing is sent until the main switch is turned on'
                      : undefined}
                    onClick={() => {
                      if (aiGlobaalUit && !aiActief) {
                        notify('AI chatbot is globally off — zet hem aan in Settings.',
                               false)
                      }
                      post('toggle-ai', { enabled: !aiActief }, 'AI', 'Saved.')
                    }}>
              {aiActief ? 'Pause AI' : 'Resume AI'}
              {aiGlobaalUit && aiActief ? ' (global off)' : ''}
            </button>
            ) : null}
          </div>

          {/* De fan-regel staat in oud ONDER de schakelaars, op een eigen
              regel met platform en aanmaakmoment erachter. */}
          <div className="mut2" style={{ fontSize: 12, marginTop: 10 }}>
            Fan: {fan}{' '}
            {/* Dezelfde wachttijd-chip als in de inbox: hoe lang deze fan al
                op antwoord wacht. */}
            {c.last_fan_message_at ? (
              <><Wacht sinds={c.last_fan_message_at} />{' '}</>
            ) : null}
            {/* Oud zet hier een potlood waarmee je de fan een eigen naam geeft. */}
            <button className="linkbtn" type="button" title="Rename this fan"
                    onClick={() => { setFanNaam(c.fan_display_name ?? '')
                                     setFanNaamOpen(true) }}>
              ✎
            </button>
            {' | '}Platform: {c.platform} | Created:{' '}
            {c.created_at ? dtSec(c.created_at) : '—'}
          </div>


          {/* Oud zet de notitie over deze fan ook in het kopblok, waar een
              chatter hem ziet zonder het fan-paneel te openen — maar ALLEEN
              als er een notitie is. Een lege plaatshouder maakt het kopblok
              tachtig pixels hoger bij elke fan zonder notitie, en dat gaat ten
              koste van de berichten eronder. In het fan-paneel staat de knop
              om er een toe te voegen. */}
          {/* De belangrijkste regel van het scherm. Dit is wat een chatter
              moet weten vóór hij begint te typen — "deze fan heeft geen geld"
              of "noem zijn ex niet". Daarom vol in beeld en niet inklapbaar.
              Eén bron: hetzelfde veld en hetzelfde endpoint als "Important
              notes" in het fan-paneel. */}
          {c.fan_custom_notes || c.model_notes ? (
            <div className="notitiebalk" title={herkomst}>
              <span className="ic" aria-hidden="true">⚠</span>
              <div className="tekst">
                {c.fan_custom_notes ? <div>{c.fan_custom_notes}</div> : null}
                {c.model_notes ? (
                  <div className="modelnotitie">
                    <b>Model</b> {c.model_notes}
                  </div>
                ) : null}
                {herkomst ? <div className="herkomst">{herkomst}</div> : null}
              </div>
              <button className="linkbtn potlood" type="button"
                      title="Edit important notes"
                      onClick={naarNotities}>✎</button>
            </div>
          ) : (
            <button type="button" className="geennotitie"
                    onClick={naarNotities}>
              <span aria-hidden="true">＋</span> No important notes — add
            </button>
          )}

        </div>
      </div>

      {/* De onderste rij pakt alles wat er na het kopblok overblijft. Beide
          kolommen zijn even hoog en scrollen van binnen; de pagina zelf
          scrollt niet. Dat is de regel: op 1920x1080 past het chatvenster in
          één scherm. */}
      {/* Embedded (Easy-stand) ligt het fan-paneel als zijpaneel over de
          modelkolom; de berichten houden dan de volle breedte. */}
      <div className={fanZichtbaar ? 'chatrij' : 'chatrij solo'}>
        {/* ---- berichtenkolom ---- */}
        <div className="panel chatkolom" data-tour="read-back">
          <div className="head">
            <div>
              {/* Oud zet hier alleen de titel; de uitsplitsing per afzender
                  staat er niet. */}
              <h3>Show Chat</h3>
            </div>
          </div>
          <div className="body flush">
            <div className="chatscroll" ref={chatScroll}>
              {/* 30-09-2026: bovenaan een leeg elementje; komt het in beeld,
                  dan laden de oudere berichten vanzelf (geen knop meer). Niet
                  sticky: een blijvende balk zou altijd "in beeld" zijn. */}
              <div ref={bovenWacht} className="bovenwacht" aria-hidden="true" />
              {q.data?.has_more && (ouderBezig || limiet >= MAX_BERICHTEN) ? (
                <div className="mut2 ouderstatus">
                  {limiet >= MAX_BERICHTEN
                    ? `Showing the latest ${MAX_BERICHTEN} messages`
                    : 'Loading older messages…'}
                </div>
              ) : null}
              {zichtbareBerichten.length === 0 ? (
                <div className="feedrow mut2" style={{ justifyContent: 'center' }}>
                  {c.platform === 'fancentro'
                    ? 'FanCentro has no messages for this contact yet.'
                    : 'No messages yet.'}
                </div>
              ) : zichtbareBerichten.map((m) => {
                // Een fooi of unlock is geen bubbel maar een regel midden in
                // het gesprek, zoals oud: bedrag, welke bijlage (met het
                // tijdstip waarop die verstuurd is), het betaalmoment, en
                // "show it" dat naar de vrijgekochte bubbel springt. De
                // koppeling (unlocks_message_id) komt van de server.
                if (m.event_type) {
                  const isTip = m.event_type === 'tip'
                  const rijId = !isTip && m.unlocks_message_id ? Number(m.unlocks_message_id) : null
                  const rij = rijId != null
                    ? messages.find((x) => x.id === rijId && !x.event_type)
                    : undefined
                  // 21-10-2026 (Jordy): hoort de vrijgekochte rij bij een bundel, dan is
                  // de bubbel in beeld de eerste rij van die bundel; daar springen we heen.
                  const doel = rij?.bundle_id
                    ? (zichtbareBerichten.find((x) => !x.event_type && x.bundle_id === rij.bundle_id) ?? rij)
                    : rij
                  const doelId = doel ? doel.id : rijId
                  const verstuurd = doel ? dtShort(doel.created_at).split(', ')[1] : null
                  const bezig = doelId != null && springDoel === doelId
                  return (
                    <div key={m.id} data-msg-id={m.id}
                         style={{ alignSelf: 'center', textAlign: 'center', margin: '2px 0' }}>
                      <span
                        role={doelId != null ? 'button' : undefined}
                        tabIndex={doelId != null ? 0 : undefined}
                        title={doelId != null ? 'Go to the message he paid for' : undefined}
                        onClick={doelId != null ? () => { setSpringFout(null); springLengte.current = -1; setSpringDoel(doelId) } : undefined}
                        onKeyDown={doelId != null ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSpringFout(null)
                            springLengte.current = -1
                            setSpringDoel(doelId)
                          }
                        } : undefined}
                        style={{
                          display: 'inline-block', padding: '4px 12px', borderRadius: 14,
                          fontSize: 12, fontWeight: 600,
                          color: isTip ? '#fbbf24' : '#34d399',
                          background: isTip ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                          border: `1px solid ${isTip ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)'}`,
                          cursor: doelId != null ? 'pointer' : 'default',
                        }}
                      >
                        {isTip ? '💶 ' : '🔓 '}{m.content}
                        {doel ? (
                          <span style={{ marginLeft: 6, opacity: 0.85, fontWeight: 400 }}>
                            ({doel.media_type || 'media'}{verstuurd ? ` sent ${verstuurd}` : ''})
                          </span>
                        ) : null}
                        <span className="mut2" style={{ marginLeft: 8, fontWeight: 400 }}>
                          {isTip ? dtShort(m.created_at) : `paid ${dtShort(m.created_at)}`}
                        </span>
                        {doelId != null ? (
                          <span style={{ marginLeft: 6, fontWeight: 800, textDecoration: 'underline' }}>
                            {bezig ? '· finding…' : '· show it'}
                          </span>
                        ) : null}
                      </span>
                      {springFout != null && springFout === doelId ? (
                        <div className="mut2" style={{ fontSize: 11, marginTop: 2 }}>
                          That message is no longer in this chat&apos;s history.
                        </div>
                      ) : null}
                    </div>
                  )
                }
                // Oud: de fan links en grijs, alles wat wíj sturen rechts en
                // groen. Dat is de kant waarop een chatter zijn eigen tekst
                // verwacht.
                const fanSide = String(m.sender).toLowerCase() === 'fan'
                return (
                  <div key={m.id} data-msg-id={m.id}
                       className={fanSide ? 'bubblerow' : 'bubblerow uit'}
                       style={uitgelicht === m.id
                         ? { outline: '2px solid #34d399', outlineOffset: 2, borderRadius: 8 }
                         : undefined}>
                    <div className="bubble">
                      <div className="bhead">
                        <span className="who">{senderName(m)}</span>
                        {/* Oud schrijft "06-09, 00:29" — zonder seconden. */}
                        <span className="mut2">{dtShort(m.created_at)}</span>
                        {/* WhatsApp-vinkjes voor een uitgaand bericht: één vinkje
                            = verzonden/onderweg (in de wachtrij), twee vinkjes =
                            door het platform bevestigd (platform_message_id, of
                            delivered gezet). Rood verschijnt ALLEEN bij een echte
                            eindfout (delivery_error), niet tijdens een gewone
                            verzending. */}
                        {!fanSide ? (
                          m.delivery_error ? (
                            <Tag kind="bad" title={m.delivery_error}>
                              Failed: {foutTekst(m.delivery_error)}
                            </Tag>
                          ) : (m.platform_message_id || m.delivered) ? (
                            <span className="afgeleverd"
                                  title="Accepted by the platform. This does not prove the fan opened it.">✓✓</span>
                          ) : (opnieuwVerstuurd.has(Number(m.id)) || resendBezig.has(Number(m.id))) ? (
                            // 04-10-2026 (Jordy): na Resend dezelfde "bezig"-stand
                            // als een nieuwe verzending, tot de worker afstempelt.
                            <span className="verzonden" title="Sending…">⏳</span>
                          ) : (
                            <span className="verzonden" title="Sent — waiting for the platform to confirm">✓</span>
                          )
                        ) : null}
                        {ppvLabel(m, c.platform) ? (
                          <Tag kind={m.is_unlocked ? 'ok' : 'warn'}>
                            {ppvLabel(m, c.platform)}
                          </Tag>
                        ) : null}
                        {m.tip_request_price ? (
                          <Tag kind={m.is_unlocked ? 'ok' : 'warn'}
                               title={c.platform === 'f2f' ? 'F2F tip request: the fan gets a "Tip me" button'
                                 : 'FanCentro has no tip request: sent as a text message'}>
                            {`Tip request · ${c.platform === 'f2f' ? '€' : '$'}${prijs(Number(m.tip_request_price))}${m.is_unlocked ? ' · paid' : ''}`}
                          </Tag>
                        ) : null}
                        {m.mass_message_log_id ? <Tag kind="acc">{massDmLabel(m.mass_message_kind)}</Tag> : null}
                        {m.kind === 'welcome' ? <Tag kind="acc">Automated welcome message</Tag> : null}
                        {/* Verwijderknop op een eigen verstuurd bericht: altijd
                            actief, geen tijdslimiet in het dashboard. De worker
                            probeert de intrekking; weigert het platform (te oud),
                            dan komt er een nette foutmelding terug. */}
                        {!fanSide && verwijderBezig.has(Number(m.id)) ? (
                          <Tag kind="warn" title="The worker is removing this message on the platform">Deleting…</Tag>
                        ) : !fanSide && !afgeleverdePpv(m) ? (
                          // 14-10-2026 (Jordy): duidelijk zichtbaar, met label; bevestiging in verwijderBericht.
                          <button className="wisbtn" type="button" data-testid="bericht-verwijderen"
                                  title="Delete this sent message for the fan (unsend on the platform)"
                                  aria-label="Delete this sent message"
                                  onClick={() => verwijderBericht(m.id)}>🗑<span className="wislabel"> Delete</span></button>
                        ) : null}
                      </div>
                      {m.bundelMedia && m.bundelMedia.length > 1 ? (
                        <div className="bundelmedia">
                          {m.bundelMedia.map((b, i) => (
                            <MessageMedia key={i} url={b.url} kind={b.type}
                                          platform={c.platform} />
                          ))}
                        </div>
                      ) : (m.mass_media ?? []).some((x) => x.url) ? (
                        // 01-10-2026 (Jordy): bij een Mass DM tonen we onze EIGEN
                        // kopie van wat er verstuurd is. De rij die we van het
                        // platform terugkrijgen heeft vaak geen bruikbare link
                        // (de F2F-chatlijst geeft alleen "[Image]"), en dan stond
                        // er "link expired" bij een foto van vijf minuten oud.
                        <div className={(m.mass_media ?? []).filter((x) => x.url).length > 1
                          ? 'bundelmedia' : undefined}>
                          {(m.mass_media ?? []).filter((x) => x.url).map((b, i) => (
                            <MessageMedia key={i} url={b.url} kind={b.type}
                                          platform={c.platform} />
                          ))}
                        </div>
                      ) : m.media_url || m.media_type ? (
                        <MessageMedia url={m.media_url} kind={m.media_type}
                                      platform={c.platform} />
                      ) : null}
                      <div className="btext">{m.content}</div>
                      {(() => {
                        // Inkomend: toon de vertaling ALTIJD als hij er is —
                        // opgeslagen of op verzoek opgehaald — los van de
                        // uitgaande toggle. Oud toonde "English: ...".
                        // De Engelse lezing van dit bericht: bij een fan de
                        // vertaling van wat hij zei, bij een uitgaand bericht
                        // dat op het platform is getypt wat er toen gezegd is.
                        const engels = m.content_english
                          || (fanSide ? m.content_translation : null)
                          || vertalingen[m.id]
                        if (engels) {
                          return <div className="btrans"><b>English: </b>{engels}</div>
                        }
                        if (fanSide) return null
                        // Uitgaand via ons dashboard: toon wat er ECHT naar de
                        // fan ging, in de taal van de fan — "Sent in <taal>: ...".
                        if (m.content_translation) {
                          return (
                            <div className="btrans">
                              {/* 14-10-2026: "en" is hier nooit de doeltaal (we vertalen VANUIT het
                                  Engels); zo'n rij kreeg vroeger ten onrechte "en" mee. */}
                              <b>{TALEN[String(m.detected_language ?? '').slice(0, 2)]
                                  && String(m.detected_language).slice(0, 2) !== 'en'
                                ? `Sent in ${TALEN[String(m.detected_language).slice(0, 2)]}: `
                                : 'Sent in translation: '}</b>
                              {m.content_translation}
                            </div>
                          )
                        }
                        return null
                      })()}
                      {/* 04-10-2026 (Jordy): onder een mislukt eigen bericht.
                          "Resend" alleen als vaststaat dat het nooit is
                          vertrokken (zie lib/verbinding); anders alleen de
                          hint om eerst op het platform te kijken. */}
                      {!fanSide && m.delivery_error ? (
                        kanOpnieuw(m) ? (
                          <div className="herverzend">
                            <button type="button" className="btn ghost sm"
                                    disabled={resendBezig.has(Number(m.id))}
                                    title="Nothing reached the platform; queue this message again"
                                    onClick={() => verstuurOpnieuw(m.id)}>
                              ↻ Resend
                            </button>
                          </div>
                        ) : (
                          <div className="herverzend mut2">
                            {opnieuwVerstuurd.has(Number(m.id))
                              ? `Resent once already. Check the chat on ${platformNaam} before sending again.`
                              : `Check the chat on ${platformNaam} before sending again.`}
                          </div>
                        )
                      ) : null}
                    </div>
                  </div>
                )
              })}
              {/* 17-09-2026: bubbels die nog onderweg zijn. Ze staan er meteen
                  (vóór het netwerk) en verdwijnen zodra de echte rij binnen is.
                  Mislukt het, dan blijft de bubbel staan met de reden. */}
              {/* Alleen recente mislukkingen (24 uur): geen oude opdrachten opnieuw aanbieden. */}
              {/* Alleen een echte lijst (een ander antwoord mag het venster nooit laten crashen). */}
              {/* 20-10-2026 (Jordy): ook opdrachten die nog lopen staan hier (in de wachtrij /
                  bezig), zodat de content na sluiten, verversen of terugkomen zichtbaar blijft;
                  "unknown" (dienst herstart) krijgt ook Retry. Komt uit de server, niet uit de browser. */}
              {(Array.isArray(mislukteMedia.data) ? mislukteMedia.data : []).filter((j) =>
                ['failed', 'unknown', 'queued', 'running'].includes(j.status)
                // created_at is naïef UTC (zonder Z): als UTC lezen.
                && Date.now() - new Date(/Z$|[+-]\d\d:\d\d$/.test(String(j.created_at ?? '')) ? String(j.created_at) : `${j.created_at}Z`).getTime() < 24 * 3600 * 1000).map((j) => (
                <div key={j.id} className="bubblerow uit" data-testid="media-opdracht" data-status={j.status}>
                  <div className="bubble bezig">
                    <div className="bmeta">
                      <span className="who">ATTACHMENT</span>
                      {j.status === 'queued' ? (
                        <Tag kind="acc">In queue{j.item_count > 1 ? ` (${j.item_count} items)` : ''}</Tag>
                      ) : j.status === 'running' ? (
                        <Tag kind="acc">{(j.message_ids ?? []).length ? 'Uploading to the platform…' : 'Preparing…'}{j.item_count > 1 ? ` (${j.item_count} items)` : ''}</Tag>
                      ) : j.status === 'unknown' ? (
                        <Tag kind="warn" title={j.error ?? ''}>Status unknown{j.item_count > 1 ? ` (${j.item_count} items)` : ''}</Tag>
                      ) : (
                        <Tag kind="bad" title={j.error ?? ''}>
                          Not delivered{j.item_count > 1 ? ` (${j.item_count} items)` : ''}
                        </Tag>
                      )}
                    </div>
                    {j.status === 'queued' || j.status === 'running' ? (
                      <div className="btext mut2" style={{ fontSize: 12 }}>
                        Sending in the background. You can close this chat; it keeps going.
                      </div>
                    ) : (
                    <div className="btext mut2" style={{ fontSize: 12 }}>{foutTekst(j.error ?? '')}</div>
                    )}
                    {j.status === 'queued' || j.status === 'running' ? null : (
                    <div className="herverzend">
                      <button type="button" className="btn ghost sm" data-testid="media-retry"
                              disabled={mediaRetryBezig === j.id}
                              title="Send the same content again as a new send (nothing goes automatically)"
                              onClick={() => mediaOpnieuw(j)}>↻ Retry</button>
                      <button type="button" className="btn ghost sm" onClick={() => mediaWeg(j.id)}>Dismiss</button>
                    </div>
                    )}
                  </div>
                </div>
              ))}
              {bezigBubbels.map((b) => (
                <div key={b.key} className="bubblerow uit">
                  <div className="bubble bezig">
                    <div className="bmeta">
                      <span className="who">SENDING</span>
                      {b.fout ? (
                        <>
                          <Tag kind="bad" title={foutTekst(b.fout)}>Failed: {foutTekst(b.fout)}</Tag>
                          {/* 06-10-2026 (Jordy): een mislukte vertaling gaat nooit
                              stilletjes onvertaald weg. 14-10: alleen opnieuw proberen. */}
                          {isVertaalFout(b.fout) && !b.media ? (
                            <>
                              <button type="button" className="btn sm" data-testid="retry-vertaling"
                                      onClick={() => verstuurViaWachtrij(
                                        { key: b.key, tekst: b.tekst, vertaal: true })}>
                                Retry translation
                              </button>
                              {/* 14-10-2026 (Jordy): geen "Send as typed" meer: nooit het Engels
                                  naar een fan die een vertaling nodig heeft. */}
                            </>
                          ) : null}
                        </>
                      ) : (
                        <span className="verzonden" title="Sending…">⏳</span>
                      )}
                    </div>
                    {b.media ? (
                      b.mediaSoort === 'video'
                        ? <video className="bmedia" src={b.media} controls preload="metadata" />
                        : b.mediaSoort === 'audio'
                          ? <audio src={b.media} controls style={{ width: '100%' }} />
                          : <img className="bmedia" src={b.media} alt="" />
                    ) : null}
                    {b.tekst ? <div className="btext">{b.tekst}</div> : null}
                  </div>
                </div>
              ))}
              <div ref={bottom} />
            </div>
          </div>

          {/* ---- invoerveld ----
               Oud houdt dit op ÉÉN regel van ongeveer 44px: de schakelaar met
               het slot, drie icoontjes, de vertaalknop, het invoerveld en een
               verzendicoon. Nieuw had er een blok van 194px van gemaakt met een
               tekstvak van drie regels en een aparte Send-knop; daardoor viel
               het chatvenster buiten het scherm en moest een chatter scrollen
               om te kunnen typen. Geen Send-knop dus, en geen aanvinkvakje:
               Enter verstuurt, Shift+Enter maakt een nieuwe regel. */}
          {/* 04-10-2026 (Jordy): één regel boven het invoerveld zolang de
              verbinding van dit model gemeten weg is. Geen meting = geen balk. */}
          {verbindingWeg ? (
            <div className="verbindbalk" role="status">
              <span className="verbindstip weg" aria-hidden="true" />
              <span data-testid="verbindbalk-tekst">{verbindingBalkTekst(c.platform, c.stage_name || c.model_slug,
                                         q.data?.messaging_stale_since, isVolledigBeheer(mij))}</span>
            </div>
          ) : null}
          {/* 21-10-2026 (Jordy): claim bij openen. Is het gesprek van een andere
              chatter, dan kan ik het lezen maar niet beantwoorden (de server weigert
              versturen ook). */}
          {inbox.stand === 'other' ? (
            <div className="verbindbalk bezetbalk" role="status" data-testid="bezet-balk">
              <span className="verbindstip" aria-hidden="true" />
              <span>Being handled by {inbox.door || 'another chatter'}. You can read this chat but not reply while they have it.</span>
            </div>
          ) : (
          <div className="composer">
            {/* 18-10-2026 (Jordy): twee standen voor de volgende bijlage, de
                actieve gemarkeerd; bij Paid is de prijs verplicht. */}
            {/* 19-10-2026 (Jordy): Free/Paid weg uit de gewone composer-balk; prijzen
                gebeurt in de Content Library (🖼). Alleen bij een eigen bestand van
                de computer (📎, owner/admin) staat de keuze er nog: zonder zou die
                PPV-upload verdwijnen. Een tekstbericht had nooit een prijs. */}
            {file ? (
            <div className="seg betaalkeuze" role="radiogroup" aria-label="Next attachment: free or paid"
                 data-testid="bijlage-betaalkeuze">
              <button type="button" role="radio" aria-checked={!paidNext}
                      className={paidNext ? undefined : 'on'} onClick={() => setPaidNext(false)}>
                🔓 Free
              </button>
              <button type="button" role="radio" aria-checked={paidNext}
                      className={paidNext ? 'on' : undefined} onClick={() => setPaidNext(true)}>
                🔒 Paid
              </button>
            </div>
            ) : null}

            {file && paidNext ? (
              <input
                className="inp prijsveld"
                type="number"
                min={c.platform === 'f2f' ? PPV_MIN_F2F_EUR : PPV_MIN_FC_CREDITS}
                title={bijlagePrijsFout ?? (c.platform === 'f2f'
                  ? `F2F minimum €${PPV_MIN_F2F_EUR}`
                  : `Minimum ${PPV_MIN_FC_CREDITS} credits · 100 credits = $1 · max 10,000`)}
                placeholder={c.platform === 'f2f' ? 'e.g. 15' : 'e.g. 500'}
                required aria-label={c.platform === 'f2f' ? 'Price in euro' : 'Price in credits'}
                aria-invalid={!!bijlagePrijsFout}
                style={bijlagePrijsFout ? { borderColor: 'var(--bad)' } : undefined}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            ) : null}
            {file && paidNext ? (
              <span className="mut2" style={{ fontSize: 12 }}>{c.platform === 'f2f' ? '€' : 'credits'}</span>
            ) : null}

            {/* 18-10-2026 (Jordy): een eigen bestand van de computer uploaden alleen
                voor owner/admin; een chatter verstuurt content uitsluitend uit
                de Content Library (de 🖼-knop hiernaast). */}
            {isBeheer(mij) ? (
              <label className="iconbtn sm" title={file ? file.name : 'Attach a file'} data-testid="bijlage-computer">
                <span aria-hidden="true">📎</span>
                <input type="file" style={{ display: 'none' }}
                       onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
            ) : null}
            {/* Oud noemt deze knop "Send from content library": hij opent de
                bibliotheek van dít model, niet een bestandskiezer. Dat is een
                ander ding dan de paperclip ernaast, die een eigen bestand
                uploadt. */}
            <button type="button" className="iconbtn sm" data-tour="content-folders"
                    title="Send from content library"
                    onClick={() => setKiezerOpen(true)}>
              <span aria-hidden="true">🖼</span>
            </button>
            {/* Emoji-knop opent een KEUZEMENU; hij plakt niet meteen één vaste
                emoji. Klikken op een emoji voegt 'm toe aan het bericht en
                sluit het menu. Dezelfde knop staat in de contentkiezer. */}
            <EmojiKnop onKies={(e) => setDraft((d) => d + e)} />
            {c.platform === 'f2f' || c.platform === 'fancentro' ? (
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <button type="button" className="iconbtn sm" data-testid="tip-vragen"
                        title={c.platform === 'f2f' ? 'Request tip (the fan gets a "Tip me" button)'
                          : 'Request tip (FanCentro: sent as a text message)'}
                        aria-expanded={tipOpen} onClick={() => setTipOpen((v) => !v)}>
                  <span aria-hidden="true">💰</span>
                </button>
                {tipOpen ? (
                  <div className="tipvraag" data-testid="tip-paneel" role="dialog" aria-label="Request tip">
                    <b style={{ fontSize: 13 }}>Request tip</b>
                    <label className="row" style={{ gap: 6, alignItems: 'center', fontSize: 12.5 }}>
                      Amount
                      <input className="inp" type="number" min={1} max={500} step="0.5" style={{ width: 90 }}
                             value={tipBedrag} onChange={(e) => setTipBedrag(e.target.value)}
                             data-testid="tip-bedrag" aria-label="Tip amount" />
                      <span className="mut2">{c.platform === 'f2f' ? '€' : '$'}</span>
                    </label>
                    <textarea className="inp" rows={2} placeholder="Message (optional)" value={tipTekst}
                              onChange={(e) => setTipTekst(e.target.value)} data-testid="tip-tekst"
                              aria-label="Tip request message" />
                    <div className="mut2" style={{ fontSize: 11 }}>
                      {c.platform === 'f2f' ? 'The fan gets a "Tip me" button in the chat.'
                        : 'FanCentro has no tip request; this goes as a normal text message.'}
                    </div>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" className="btn ghost sm" onClick={() => setTipOpen(false)}>Cancel</button>
                      <button type="button" className="btn primary sm" data-testid="tip-versturen"
                              disabled={tipBezig || !(Number(tipBedrag) >= 1 && Number(tipBedrag) <= 500)}
                              onClick={() => { void vraagTip() }}>Send request</button>
                    </div>
                  </div>
                ) : null}
              </span>
            ) : null}

            {file ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => { setFile(null); setPaidNext(false); setPrice('') }}>
                {file.name.slice(0, 18)} ✕
              </button>
            ) : null}

            {/* Oud zet op deze knop de TAAL waarin het bericht straks vertrekt
                — "→ Turkish" — en niet alleen aan of uit. */}
            {/* 06-10-2026 (Jordy): de tooltip zegt waarom hij aan of uit staat. */}
            {/* 15-10-2026 (Jordy): op F2F vast aan (grijs, niet uit te zetten). */}
            {/* 20-10-2026 (Jordy): een klik opent een menu met alle standen, zodat de
                chatter ook vanuit "off" terug kan naar "→ Dutch". De keuze geldt voor
                dit gesprek; zonder keuze blijft de standaard van de server. */}
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <button className="btn ghost sm" type="button"
                      title={knop.titel} aria-label={knop.titel} data-testid="vertaalknop"
                      aria-haspopup="menu" aria-expanded={vertaalMenu}
                      disabled={knop.vast} aria-disabled={knop.vast}
                      style={knop.vast ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                      onClick={() => { if (!knop.vast) setVertaalMenu((o) => !o) }}>
                {knop.label}
              </button>
              {vertaalMenu && !knop.vast ? (
                <>
                  <span style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setVertaalMenu(false)} />
                  <span role="menu" data-testid="vertaalmenu" className="panel"
                        style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, zIndex: 41,
                                 display: 'grid', gap: 2, padding: 4, minWidth: 230 }}>
                    {keuzeMenu(c?.outgoing_translation_default ?? terugval(c?.platform), c?.platform).map((m) => {
                      const gekozen = (handmatig === null ? 'auto' : handmatig === false ? 'uit'
                        : handmatig === true ? 'auto' : handmatig) === m.keuze
                      return (
                        <button key={m.keuze} type="button" role="menuitemradio" aria-checked={gekozen}
                                data-testid={`vertaal-${m.keuze}`} className={gekozen ? 'btn sm' : 'btn ghost sm'}
                                style={{ justifyContent: 'flex-start', textAlign: 'left' }} title={m.uitleg}
                                onClick={() => { setHandmatig(m.keuze === 'auto' ? null : m.keuze); setVertaalMenu(false) }}>
                          {m.label}
                        </button>
                      )
                    })}
                  </span>
                </>
              ) : null}
            </span>

            <input
              className="inp veld"
              placeholder="Type a message..."
              // 19-10-2026 (Jordy): alleen mobiel — bij focus (toetsenbord komt op) naar de bodem.
              onFocus={mobiel ? () => { for (const t of [50, 250, 500]) window.setTimeout(mobielNaarBodem, t) } : undefined}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); verstuur() }
              }}
            />
            <button type="button" className="iconbtn sm verzend"
                    title={file
                      ? (paidNext
                        ? `Send (Paid · ${String(price).trim()
                            ? (c.platform === 'f2f' ? `€${price}` : credits(price)) : 'set a price'})`
                        : 'Send (Free)')
                      : 'Send'}
                    aria-label={file ? (paidNext ? 'Send (Paid)' : 'Send (Free)') : 'Send'}
                    disabled={(!draft.trim() && !file) || busy === 'Send' || !!bijlagePrijsFout}
                    onClick={verstuur}>
              <span aria-hidden="true">➤</span>
            </button>
          </div>
          )}
        </div>

        {/* ---- fan-paneel ----
             Oud heeft drie tabbladen: Fan info, Transactions en History.
             Onder Fan info staan Model info en Fan info NAAST elkaar.

             Bewuste afwijking, op verzoek van Jordy: geen interne scrollbalk.
             Alle velden van beide kaarten staan tegelijk in beeld; het paneel
             mag daarvoor hoger worden en de pagina scrollt mee. Een chatter
             hoeft zo niet te scrollen om te zien wat hij over model en fan
             moet weten. */}
        {/* Oud zet de knop BOVEN het paneel, niet in de kopbalk. Daardoor
            begint het paneel 34px lager dan de berichtenkolom, en staat de
            knop waar hij hoort: bij wat hij weg- en terugklapt. */}
        {fanZichtbaar ? (
          <div className={mobiel ? `fankolom m-lade m-${mobielPaneel}` : 'fankolom'}>
            <div className="row" style={{ justifyContent: 'flex-end',
                                          marginBottom: 8 }}>
              {mobiel ? (
                <button className="btn sm m-ladesluit" type="button"
                        onClick={() => setMobielPaneel(null)}>
                  ✕ Close {mobielPaneel === 'model' ? 'model info' : 'fan info'}
                </button>
              ) : null}
              <button className="btn ghost sm" type="button"
                      onClick={() => setShowFan(false)}>
                Hide fan panel
              </button>
            </div>
          <div className="panel fanpanel">
            <div className="head">
              <div>
                <h3>Model &amp; Fan info</h3>
                <p>{fan} · {c.platform}</p>
              </div>
              <div className="r">
                <div className="seg" data-tour="fan-tabs">
                  <button type="button" role="tab" aria-selected={tab === 'fan'}
                          className={tab === 'fan' ? 'on' : undefined}
                          onClick={() => setTab('fan')}>Model &amp; Fan</button>
                  <button type="button" role="tab" aria-selected={tab === 'transactions'}
                          className={tab === 'transactions' ? 'on' : undefined}
                          onClick={() => setTab('transactions')}>Transactions</button>
                  <button type="button" role="tab" aria-selected={tab === 'history'}
                          className={tab === 'history' ? 'on' : undefined}
                          onClick={() => setTab('history')}>History</button>
                </div>
              </div>
            </div>
            {/* 16-10-2026 (Jordy): Fan info en Model info binnen de vaste
                paneelhoogte — boven vast, midden scrollt, onder vast.
                18-10-2026: Model links, Fan rechts (FanInfoPaneel). */}
            <div className={tab === 'fan' ? 'body vast' : 'body'}>
              {tab === 'fan' ? (
                <FanInfoPaneel
                  conversationId={id ?? ''}
                  info={c.fan_info}
                  notities={c.fan_custom_notes}
                  bijnaam={c.fan_display_name ?? c.fan_username ?? ''}
                  fanId={c.fan_username || c.fan_id || ''}
                  lokaleTijd={fanLokaleTijd}
                  laatstActief={c.fan_last_active_at ? dtSec(c.fan_last_active_at) : '—'}
                  status={fanStatus}
                  onSaved={q.refetch}
                  notitiesElders
                  notitiesBlok={
                    // 18-10-2026 (Jordy): FAN-notities in de fan-kolom, direct onder
                    // Status; één bron met de gele balk boven de chat.
                    <ImportantNotes conversationId={id ?? ''} notities={c.fan_custom_notes}
                                    focusSleutel={notitieFocus} onSaved={q.refetch} />
                  }
                  modelBoven={
                    <ModelKop
                      naam={c.stage_name ?? c.model_slug}
                      avatarUrl={c.avatar_url}
                      onGids={c.model_persona_id ? () => setGidsOpen(true) : undefined}
                    />
                  }
                  modelMidden={<>
                    {/* 18-10-2026 (Jordy): Do's en Don'ts bovenaan, dan de balken.
                        Important notes staan in de fan-kolom (onder Status). */}
                    <ModelBovenblok
                      personaId={c.model_persona_id}
                      info={c.model_info}
                      engels={modelEngels}
                      onSaved={q.refetch}
                    />
                    <ModelInfoSectie
                      personaId={c.model_persona_id}
                      info={c.model_info}
                      engels={modelEngels}
                      onAntwoord={zetStandaardAntwoord}
                      notities={c.model_notes}
                      onSaved={q.refetch}
                    />
                  </>}
                  bodem={<>
                    {/* In oud staat dit binnen de fan-kaart; het gaat over deze
                        fan, niet over het gesprek. */}
                    <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>
                      Models this fan is speaking to
                    </div>
                    <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                      {(c.fan_model_chats ?? []).length === 0
                        ? <span className="mut2" style={{ fontSize: 12 }}>
                            Only this chat.
                          </span>
                        : (c.fan_model_chats ?? []).map((x) => {
                            const hier = x.conversation_id === c.id
                            return (
                              <button
                                key={x.conversation_id}
                                type="button"
                                className={hier ? 'btn sm' : 'btn ghost sm'}
                                disabled={hier}
                                onClick={() => openGesprek(x.conversation_id)}
                              >
                                {x.model_name || x.model_slug}{hier ? ' · current' : ''}
                              </button>
                            )
                          })}
                    </div>

                    {/* --- abonnement en bestedingen naast elkaar (compact) --- */}
                    <div className="fanonder-rij">
                    <div>
                    <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>
                      Subscription
                    </div>
                    <div className="kv">
                      <span>Status</span><span>{c.fan_subscription_status ?? '—'}</span>
                      <span>Last paid</span>
                      <span>
                        {/* 23-10-2026 (Jordy): het echte betaalmoment, niet het importmoment. */}
                        {c.fan_subscription_last_paid_at ? dtSec(c.fan_subscription_last_paid_at) : '—'}
                      </span>
                      <span>Renew</span>
                      <span>{c.fan_subscription_renews ?? '—'}</span>
                    </div>
                    </div>

                    {/* --- bestedingen, met de vier getallen die oud toont --- */}
                    <div data-tour="spending">
                    <div className="row" style={{ alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 12.5 }}>Spending behavior</span>
                      {isSpender(c) ? <Tag kind="warn">★ Spender</Tag> : null}
                    </div>
                    {/* 20-10-2026 (Jordy): ook een spender als hij alleen bij een ander model betaalde. */}
                    {isSpender(c) && Number(c.fan_total_spend || 0) <= 0 ? (
                      <div data-testid="spender-elders" style={{ fontSize: 12, marginBottom: 6, color: 'var(--warn)' }}>
                        Spender: spent {eur(alleModellen(c))} on other models (€0 with this model yet).
                      </div>
                    ) : null}
                    {/* 19-10-2026 (Jordy): eerst over ALLE modellen (altijd zichtbaar),
                        dan de bestaande regels van dit model. */}
                    <div className="kv" data-testid="spending-behavior">
                      <span><b>Total spend (all models)</b></span>
                      <span className="mono" data-testid="spend-alle-modellen"><b>{eur(alleModellen(c))}</b></span>
                      <span>Total spend (this model)</span>
                      <span className="mono">{eur(c.fan_total_spend)}</span>
                      <span>Tips (this model)</span>
                      <span className="mono">{fooien === null ? '—' : eur(fooien)}</span>
                      <span>Highest paid (this model)</span>
                      <span className="mono">{eur(c.fan_highest_purchase)}</span>
                      <span>Lowest paid (this model)</span>
                      <span className="mono">{eur(c.fan_lowest_purchase)}</span>
                    </div>
                    {/* 19-10-2026 (Jordy): alleen modellen met spend; niets = geen uitsplitsing. */}
                    {perModel(c).length > 0 ? (
                      <div className="spendpermodel" data-testid="spend-per-model">
                        {perModel(c).map((m) => (
                          <span key={m.naam} className={m.bedrag > 0 ? 'betaald' : undefined}>
                            {m.naam}{m.hier ? ' (this chat)' : ''} <b className="mono">{eur(m.bedrag)}</b>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {String(c.platform).toLowerCase() === 'f2f' ? (
                      <div className="mut2" style={{ fontSize: 10.5, marginTop: 4 }} data-testid="spend-f2f-melding">
                        F2F payments under a display name that is not linked to this fan are not counted.
                      </div>
                    ) : null}
                    </div>
                    </div>
                  </>}
                />
              ) : tab === 'transactions' ? (
                // De transactietabel hoort onder dit tabblad, niet onder Fan info.
                // 12-10-2026 (Jordy): in dezelfde kaart als Fan info, zodat de
                // tabbladen gelijk uitlijnen.
                <div className="fankaarten fankaarten-een" data-testid="fan-tab-transactions">
                  <div className="subblock">
                    <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <b style={{ fontSize: 13 }}>Transactions</b>
                    </div>
                    {/* 15-10-2026 (Jordy): weer zoals oud — chips, Spent on, Tips vs
                        PPV, Latest transactions met datum en soort. */}
                    <FanTransacties c={c} />
                  </div>
                </div>
              ) : (
                <div className="fankaarten fankaarten-een" data-testid="fan-tab-history">
                  <div className="subblock">
                    <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <b style={{ fontSize: 13 }}>History</b>
                    </div>
                    {/* 15-10-2026 (Jordy): weer zoals oud — Unlock history en Content
                        history met Resend. De lijst met andere chats van deze fan
                        staat alleen onder Fan info ("Models this fan is speaking to"). */}
                    <FanHistorie
                      c={c}
                      onSpring={(id) => { setSpringFout(null); springLengte.current = -1; setSpringDoel(id) }}
                      onResent={q.refetch}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        ) : null}
      </div>

      {kiezerOpen && c ? (
        <ContentPicker
          conversationId={Number(id)}
          modelSlug={c.model_slug}
          platform={c.platform}
          onClose={() => setKiezerOpen(false)}
          onVerstuurd={q.refetch}
        />
      ) : null}

      {mobiel && teVerwijderen != null ? (
        <MobielBevestig
          tekst="Delete this message for the fan? It is unsent on the platform and cannot be undone."
          ja="Delete" nee="Cancel" gevaar
          onNee={() => setTeVerwijderen(null)}
          onJa={() => { const mid = teVerwijderen; setTeVerwijderen(null); void verwijderBericht(mid, true) }}
        />
      ) : null}
      {fanNaamOpen ? (
        <Modal
          title="Rename fan"
          desc="This name is only for the dashboard; the fan never sees it."
          width={420}
          onClose={() => setFanNaamOpen(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setFanNaamOpen(false)}>Cancel</button>
              <button className="btn primary sm" type="button"
                      disabled={busy === 'Rename fan'}
                      onClick={() => run({
                        label: 'Rename fan',
                        // De route heet /fan-dashboard-name; /fan-name gaf 405.
                        request: mut.patch(
                          `/api/admin/conversations/${id}/fan-dashboard-name`,
                          { display_name: fanNaam }),
                        onDone: () => { setFanNaamOpen(false); q.refetch() },
                        successText: 'Name saved.',
                      })}>
                Save
              </button>
            </>
          }
        >
          <input className="inp" value={fanNaam} placeholder="Nickname"
                 onChange={(e) => setFanNaam(e.target.value)} />
        </Modal>
      ) : null}

      {assignOpen ? (
        <Modal
          title="Assign to chatter"
          desc="A private chat can go to several chatters at once. Leave it empty and save to release the chat again."
          width={460}
          onClose={() => setAssignOpen(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setAssignOpen(false)}>Cancel</button>
              <button
                className="btn primary sm"
                type="button"
                disabled={busy === 'Assign'}
                onClick={() => run({
                  label: 'Assign',
                  request: assignPick.length
                    ? mut.post(`/api/admin/conversations/${id}/claim`,
                               { admin_user_ids: assignPick })
                    : mut.post(`/api/admin/conversations/${id}/release`),
                  onDone: () => { setAssignOpen(false); q.refetch() },
                  successText: assignPick.length ? 'Assigned.' : 'Released.',
                })}
              >
                Save
              </button>
            </>
          }
        >
          <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {(chatters.data?.chatters ?? []).map((x) => (
              <label className="chkrow" key={x.admin_user_id}>
                <span
                  className={assignPick.includes(x.admin_user_id) ? 'chk on' : 'chk'}
                  onClick={() => setAssignPick((p) => p.includes(x.admin_user_id)
                    ? p.filter((n) => n !== x.admin_user_id)
                    : [...p, x.admin_user_id])}
                >
                  {assignPick.includes(x.admin_user_id) ? '✓' : ''}
                </span>
                {x.name}{x.is_me ? ' (me)' : ''}
              </label>
            ))}
          </div>
        </Modal>
      ) : null}

      {gidsOpen && c?.model_persona_id ? (
        <Modal
          title={`Persona guide · ${c.stage_name ?? c.model_slug}`}
          width={720}
          onClose={() => setGidsOpen(false)}
          footer={<button className="btn ghost sm" type="button"
                          onClick={() => setGidsOpen(false)}>Close</button>}
        >
          <div style={{ maxHeight: 'calc(65vh / var(--dz, 1))', overflowY: 'auto' }}>
            <PersonaGids personaId={c.model_persona_id} />
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
